import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import {
  ClipboardCheck, RefreshCw, Download, Search, CheckCircle2, Clock, UserRound, Eye, XCircle, AlertCircle, Radio, RotateCcw,
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import AplusPanel from './AplusPanel';
import { INTEGRATOR_SYSTEMS, channelIntegrator, aggregateWrites, isAllegroDirect, allegroOfferUrl } from '../lib/integrator';
import { setUrlParams } from '../lib/urlState';
import { onOrdersChanged } from '../lib/ordersWatch';
import { cn, formatNumber, formatDateTime } from '../lib/utils';
import { loadWithCache, invalidate, getCached } from '../lib/cache';
import PageHeader from './ui/PageHeader';
import { Card, CardHeader, CardTitle, CardDescription, CardBody } from './ui/Card';
import { Input } from './ui/Input';
import Button from './ui/Button';
import Badge from './ui/Badge';
import EmptyState from './ui/EmptyState';
import { SkeletonRow } from './ui/Skeleton';
import { Select } from './ui/Select';
import { countExportDone, exportKind, exportProblems } from '../lib/exportStatus';
import { fetchWritesInfo } from '../lib/writesInfo';

/**
 * GOTOWOŚĆ (plan v6 E1 / T10, czat 2, 2026-10-14). Kontrakt K3.
 *
 * Ekran TYLKO CZYTA tabelę `listings` — nie liczy niczego sam. Worker (czat 1, T09) zapisuje
 * po biegu jeden wiersz na produkt × kanał: status (live / ready / waiting_client / review /
 * rejected / error) + reasons [{code, detail}] + kategoria + card_exists + cena.
 * Zastępuje ręczny `raport_gotowosci.py` (xlsx jednym kliknięciem).
 *
 * Odczyt jest ŁAGODNY: dopóki migracja K3 (czat 1) nie doda kolumn `order_id/channel_id/platform/
 * market/status/reasons`, ekran mapuje stare kolumny z 0008 (marketplace/storefront/situation/
 * situation_reason/is_live), więc nie wywraca się na starym kształcie.
 */

const STATUS = {
  live:           { label: 'Żywe',               short: 'żywe',       tone: 'success', icon: Radio,         hint: 'Oferta widoczna na platformie, potwierdzona przez API.' },
  ready:          { label: 'Gotowe',             short: 'gotowe',     tone: 'brand',   icon: CheckCircle2,  hint: 'Wszystko jest — czeka na „Publikuj".' },
  waiting_client: { label: 'Czeka na klienta',   short: 'klient',     tone: 'warning', icon: UserRound,     hint: 'Brakuje danych od klienta: WEEE, EAN, kontakt GPSR, zdjęcie.' },
  review:         { label: 'Do przeglądu',       short: 'przegląd',   tone: 'info',    icon: Eye,           hint: 'Kategoria z podpowiedzi, brak parametrów albo bramka treści.' },
  rejected:       { label: 'Odrzucone',          short: 'odrzucone',  tone: 'danger',  icon: XCircle,       hint: 'Platforma odrzuciła — powód z API.' },
  error:          { label: 'Błąd',               short: 'błąd',       tone: 'danger',  icon: AlertCircle,   hint: 'Błąd po naszej stronie.' },
};
const STATUS_ORDER = ['live', 'ready', 'waiting_client', 'review', 'rejected', 'error'];
const TONE_ICON = { success: 'text-success', brand: 'text-brand-600 dark:text-brand-400', warning: 'text-warning', danger: 'text-danger', info: 'text-info' };

const REASON_LABEL = {
  weee: 'numer WEEE', battg: 'numer BattG', ean: 'EAN', gpsr_contact: 'kontakt GPSR producenta', image: 'zdjęcie', price: 'cena',
  category_suggested: 'kategoria z podpowiedzi', attributes_missing: 'brakujące parametry', content_gate: 'bramka treści',
  // v9/B4: powody z silnika, które dotąd pokazywały się jako surowy kod
  missing_attribute: 'parametr wymagany przez Kaufland', ean_duplicate: 'zduplikowany EAN', platform: 'odpowiedź platformy',
  platform_rejected: 'odrzucone przez platformę', content_gate_fixed: 'poprawione automatycznie', ean_check_failed: 'sprawdzenie EAN',
  checks_unavailable: 'brak sprawdzeń dla platformy', attributes_not_checked: 'parametry nie sprawdzone', generation_failed: 'błąd generowania',
  offer_created: 'oferta utworzona', product_data_sent: 'wysłane na platformę',
  // 21.10: literówki w danych klienta (norma z przyszłości, różne kody modelu) — informacja, status bez zmian
  source_check: 'do sprawdzenia u klienta',
};

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const KAUFLAND_CATEGORIES_URL = `${SUPABASE_URL}/functions/v1/kaufland-categories`;
const PUBLISH_ORDER_URL = `${SUPABASE_URL}/functions/v1/publish-order`;
const CONNECTIONS_TEST_URL = `${SUPABASE_URL}/functions/v1/connections-test`;
const REGENERATE_URL = `${SUPABASE_URL}/functions/v1/regenerate-product`;

async function callEdge(url, payload) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }, body: JSON.stringify(payload) });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
  return d;
}

/** D (24.10): stan zapisu przez RPC order_writes_info (t76), awaryjnie edge — z limitem czasu 8 s. */
const loadWritesInfo = (orderIds) => fetchWritesInfo(orderIds, {
  rpc: (ids) => supabase.rpc('order_writes_info', { p_order_ids: ids }),
  edgeInfo: (id) => callEdge(PUBLISH_ORDER_URL, { order_id: id, action: 'info' }),
});

const PLATFORM_LABEL = { kaufland: 'Kaufland', allegro: 'Allegro', amazon: 'Amazon', base_channel: 'BaseLinker', sellasist_channel: 'Sellasist' };

/** Stary kształt (0008) → K3. Nowe kolumny mają pierwszeństwo, gdy już są. */
function normalize(row) {
  const platform = (row.platform || row.marketplace || '').toString().toLowerCase();
  const market = (row.market || row.storefront || '').toString().toLowerCase();
  let status = row.status;
  if (!status) {
    if (row.is_live) status = 'live';
    else if (row.situation === 'visible') status = 'live';
    else if (row.situation) status = 'review';
    else status = 'ready';
  }
  let reasons = row.reasons;
  if (!Array.isArray(reasons)) reasons = row.situation_reason ? [{ code: 'platform', detail: row.situation_reason }] : [];
  const price = row.price != null ? Number(row.price) : row.price_cents != null ? row.price_cents / 100 : null;
  // 2.4 (plan v12): cena z pliku klienta (bez reguły kanału) — osobno od ceny oferty.
  const sourcePrice = row.source_price != null ? Number(row.source_price) : null;
  return {
    id: row.id, order_id: row.order_id ?? null, channel_id: row.channel_id ?? null,
    sku: row.sku || '', ean: row.ean || '', row_index: row.row_index ?? null, platform, market, status, reasons,
    category_id: row.category_id ?? null, category_origin: row.category_origin ?? null,
    card_exists: row.card_exists ?? null, price, currency: row.currency || 'EUR',
    source_price: sourcePrice, source_currency: row.source_currency || '',
    platform_offer_id: row.platform_offer_id ?? null,
    updated_at: row.updated_at,
  };
}

/** Kolejność z pliku klienta (row_index; worker zapisuje do BASE/Sellasist w tej samej kolejności). Bez row_index — na końcu. */
function byFileOrder(a, b) {
  const ai = a.row_index ?? Infinity; const bi = b.row_index ?? Infinity;
  if (ai !== bi) return ai < bi ? -1 : 1;
  return (a.sku || '').localeCompare(b.sku || '');
}

/**
 * Raport braków DLA KLIENTA (plan v7 §7.5): tylko produkty „czeka na klienta", powody po ludzku —
 * co uzupełnić i gdzie — zamiast kodów. Plik idzie do klienta bez obróbki.
 */
const CLIENT_FIX = {
  weee: { what: 'Numer rejestracji WEEE (Niemcy)', how: 'Produkt jest sprzętem elektrycznym — w Niemczech sprzedawca musi mieć numer z rejestru stiftung ear (https://www.stiftung-ear.de). Prześlij nam numer w formacie „WEEE-Reg.-Nr. DE 12345678". Jeden numer odblokowuje wszystkie takie produkty.' },
  battg: { what: 'Numer rejestracji baterii BattG (Niemcy)', how: 'Produkt zawiera baterie — potrzebny numer z rejestru baterii stiftung ear (https://www.stiftung-ear.de). Jeden numer odblokowuje wszystkie takie produkty.' },
  ean: { what: 'Kod EAN', how: 'Brakuje kodu EAN albo jest błędny. Wpisz prawidłowy 13-cyfrowy kod z opakowania produktu (w sklepie: pole „kod producenta / EAN").' },
  gpsr_contact: { what: 'Dane producenta (GPSR)', how: 'Unijne przepisy GPSR wymagają danych producenta: pełna nazwa firmy, adres pocztowy i adres e-mail. Uzupełnij je przy producencie w sklepie albo prześlij nam.' },
  image: { what: 'Zdjęcie produktu', how: 'Dodaj co najmniej jedno zdjęcie produktu (najlepiej na białym tle, min. 500 × 500 px) w sklepie.' },
  price: { what: 'Cena', how: 'Brakuje ceny sprzedaży albo nie da się jej policzyć. Uzupełnij cenę produktu w sklepie.' },
  // v9/B4
  missing_attribute: { what: 'Parametr wymagany przez Kaufland', how: 'Kaufland wymaga parametru, którego nie ma w danych produktu (szczegóły obok). Dopisz go w opisie lub w osobnej kolumnie pliku/sklepu — przy następnym przebiegu uzupełnimy go automatycznie.' },
  ean_duplicate: { what: 'Ten sam EAN przy kilku produktach', how: 'Jeden EAN = jeden produkt. Popraw EAN albo usuń duplikat w pliku/sklepie — Kaufland za nadużycie EAN usuwa oferty.' },
  platform_rejected: { what: 'Kaufland odrzucił kartę', how: 'Automatyczna poprawka nie wystarczyła — szczegóły obok. Uzupełnij wskazane dane albo skontaktuj się z nami.' },
};
function clientFix(r) {
  const f = CLIENT_FIX[r.code];
  const withDetail = ['gpsr_contact', 'missing_attribute', 'ean_duplicate', 'platform_rejected'].includes(r.code) && r.detail;
  if (f) return { what: f.what, how: f.how + (withDetail ? ` (${r.detail})` : '') };
  return { what: REASON_LABEL[r.code] || 'Inne', how: r.detail || 'Skontaktuj się z nami — wyjaśnimy, czego brakuje.' };
}

function reasonText(r) {
  const base = REASON_LABEL[r.code] || r.code || '';
  return r.detail ? `${base}${base ? ': ' : ''}${r.detail}` : base;
}

/**
 * T14-UI: wybór kategorii Kauflanda dla produktu → zapis do mapy kategorii klienta (`category_map`,
 * origin = client) pod węzłem sklepu z faktów (`kategoria_w_sklepie`). Worker czyta mapę PRZED podpowiedzią
 * przy następnym biegu — raz zmapowany węzeł = na zawsze. Sam wiersz `listings` zmienia tylko silnik.
 */
/**
 * SAAS-REGEN (plan v13 §5 pkt 4.3, 21.10.2026): regeneracja jednego produktu po zakończonym zleceniu.
 * Krok 1 — wycena z bazy (`regen_quote`): błąd programu = darmowe; pula 1 % planu = darmowe; inaczej płatne (cena jak za ofertę).
 * Krok 2 — „Generuj”: `regen_claim` (pobiera kredyty, gdy płatne) + task workera; nowa treść trafia do pliku w Historii,
 * gotowość przelicza się po biegu. Bez własnej logiki treści — ten sam pipeline co zwykły bieg.
 */
const REGEN_CAUSE = {
  product_failed: 'produkt zakończył się błędem silnika',
  no_result: 'produkt nie ma wyniku',
  error_marker: 'wynik zawiera marker błędu („Błąd…”)',
  previous_regen_failed: 'poprzednia regeneracja nie powiodła się',
};
function RegenDialog({ row, order, onClose }) {
  const orderId = row.order_id || order?.id;
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [regen, setRegen] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const q = await callEdge(REGENERATE_URL, { order_id: orderId, row_index: row.row_index, mode: 'quote' });
        if (!alive) return;
        if (q?.ok === false) setError(q.error === 'order_not_done' ? 'Zlecenie nie jest zakończone — regeneracja działa po biegu.' : q.error === 'regen_in_progress' ? 'Ten produkt właśnie się regeneruje — poczekaj na wynik.' : q.error === 'forbidden' ? 'Brak dostępu do tego zlecenia.' : q.error);
        else setQuote(q);
      } catch (e) { if (alive) setError(e.message); }
    })();
    return () => { alive = false; };
  }, [row, orderId]);

  // Po uruchomieniu: odpytujemy status regeneracji co 4 s (do 3 min).
  useEffect(() => {
    if (!result?.id) return undefined;
    let n = 0;
    const t = setInterval(async () => {
      n += 1;
      const { data } = await supabase.from('regenerations').select('status, error, finished_at').eq('id', result.id).maybeSingle();
      if (data) setRegen(data);
      if (!data || data.status === 'done' || data.status === 'failed' || n > 45) clearInterval(t);
    }, 4000);
    return () => clearInterval(t);
  }, [result]);

  const run = async () => {
    setBusy(true);
    try {
      const r = await callEdge(REGENERATE_URL, { order_id: orderId, row_index: row.row_index, mode: 'run', note: note.trim() || undefined });
      if (r?.ok === false) throw new Error(r.error === 'insufficient_credits' ? 'Za mało kredytów na regenerację.' : r.error);
      setResult(r);
      invalidate('history:');
      toast.success(r.credits > 0 ? `Regeneracja uruchomiona — pobrano ${r.credits} kr.` : 'Regeneracja uruchomiona (bez opłaty).');
    } catch (e) { toast.error(e.message === 'insufficient_credits' ? 'Za mało kredytów na regenerację.' : e.message); }
    finally { setBusy(false); }
  };

  const pool = quote?.pool;
  const reasonLine = quote?.reason === 'program_error'
    ? <>Bez opłaty — <b>błąd programu</b> ({REGEN_CAUSE[quote.cause] || quote.cause}).</>
    : quote?.reason === 'pool'
      ? <>Bez opłaty — <b>z puli regeneracji</b> planu{pool?.unlimited ? ' (bez limitu)' : ` (${pool.used + 1} z ${pool.limit} w tym okresie)`}.</>
      : quote?.reason === 'paid'
        ? <><b>Płatna</b> — {quote.credits} {quote.credits === 1 ? 'kredyt' : quote.credits < 5 ? 'kredyty' : 'kredytów'} (jak za ofertę){pool?.limit > 0 ? `; pula ${pool.limit} regeneracji na okres jest wyczerpana` : ''}.</>
        : null;

  return (
    <div className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <CardHeader className="flex items-start justify-between gap-3">
          <div>
            <CardTitle>Wygeneruj ponownie „{row.sku || `wiersz ${row.row_index}`}”</CardTitle>
            <CardDescription>Nowa treść zastąpi obecną w pliku wynikowym (Historia) i w gotowości. Poprzednia wersja zostaje w dzienniku regeneracji.</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}><XCircle className="w-4 h-4" /></Button>
        </CardHeader>
        <CardBody className="grid gap-3">
          {error && <p className="text-sm text-rose-700 dark:text-rose-300 flex items-start gap-2"><AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />{error}</p>}
          {!error && !quote && <p className="text-sm text-ink-500">Sprawdzam, czy regeneracja jest płatna…</p>}
          {quote && !result && (
            <>
              <p className="text-sm text-ink-800 dark:text-ink-100">{reasonLine}</p>
              <label className="grid gap-1 text-xs text-ink-500 dark:text-ink-400">
                Uwaga do dziennika (opcjonalnie — co było nie tak)
                <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} maxLength={500} className="rounded-lg border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 px-3 py-2 text-sm text-ink-900 dark:text-ink-50" placeholder="np. pomylony model w zawartości opakowania" />
              </label>
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={onClose}>Anuluj</Button>
                <Button onClick={run} loading={busy} leftIcon={<RotateCcw className="w-4 h-4" />}>{quote.credits > 0 ? `Generuj za ${quote.credits} kr.` : 'Generuj bez opłaty'}</Button>
              </div>
            </>
          )}
          {result && (
            <div className="text-sm text-ink-800 dark:text-ink-100 grid gap-2">
              <p className="flex items-center gap-2">
                {regen?.status === 'done' ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : regen?.status === 'failed' ? <XCircle className="w-4 h-4 text-rose-600" /> : <RefreshCw className="w-4 h-4 animate-spin text-brand-600" />}
                {regen?.status === 'done' ? 'Gotowe — nowa treść jest w pliku wynikowym (Historia → pobierz) i w gotowości.' : regen?.status === 'failed' ? `Nie udało się: ${regen.error || 'błąd'}${result.credits > 0 ? ' — kredyty zwrócone.' : ''}` : 'Generuję… zwykle 20–60 s. Możesz zamknąć okno — wynik i tak się zapisze.'}
              </p>
              <div className="flex justify-end"><Button variant="secondary" onClick={onClose}>Zamknij</Button></div>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function CategoryPicker({ row, order, onClose, onSaved }) {
  const [sourceNode, setSourceNode] = useState(null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('product_facts').select('value').eq('order_id', row.order_id).eq('sku', row.sku).eq('key', 'kategoria_w_sklepie').limit(1);
      const node = data?.[0]?.value || null;
      setSourceNode(node);
      if (node && order?.tenant_id) {
        const { data: m } = await supabase.from('category_map').select('*').eq('tenant_id', order.tenant_id).eq('platform', row.platform).eq('market', row.market).eq('source_node', node).maybeSingle();
        setExisting(m || null);
      }
    })();
  }, [row, order]);

  const search = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setBusy(true);
    try {
      const d = await callEdge(KAUFLAND_CATEGORIES_URL, { tenant_id: order?.tenant_id || undefined, storefront: row.market || 'de', q: q.trim(), limit: 20 });
      setResults(d.categories || []);
      if (!d.categories?.length) toast.info('Kaufland nie znalazł kategorii dla tej frazy — spróbuj po niemiecku, np. „Kabelkanal".');
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  const pick = async (c) => {
    if (!order?.tenant_id) return toast.error('To zlecenie nie ma klienta — mapę kategorii zapisuje się per klient.');
    if (!sourceNode) return toast.error('Produkt nie ma „kategorii w sklepie" w katalogu — nie ma do czego przypiąć mapy.');
    setSaving(true);
    try {
      const rowMap = { tenant_id: order.tenant_id, platform: row.platform, market: row.market, source_node: sourceNode, category_id: String(c.id), category_name: c.path || c.name, origin: 'client' };
      const { error } = await supabase.from('category_map').upsert(rowMap, { onConflict: 'tenant_id,platform,market,source_node' });
      invalidate('readiness:rows:');
      if (error) throw error;
      toast.success(`Zapisano: „${sourceNode}" → ${c.name}. Zadziała przy następnym biegu tego klienta.`);
      onSaved?.();
      onClose();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-2xl" onClick={e => e.stopPropagation()}>
        <CardHeader className="flex items-start justify-between gap-3">
          <div>
            <CardTitle>Kategoria Kauflanda dla „{row.sku}"</CardTitle>
            <CardDescription>
              Węzeł w sklepie klienta: <b>{sourceNode === null ? '…' : sourceNode || 'brak w katalogu'}</b>
              {row.category_id && <> · dziś: {row.category_id}{row.category_origin === 'suggested' ? ' (podpowiedź)' : ''}</>}
              {existing && <> · w mapie: <b>{existing.category_name || existing.category_id}</b> ({existing.origin})</>}
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}><XCircle className="w-4 h-4" /></Button>
        </CardHeader>
        <CardBody className="grid gap-3">
          <form onSubmit={search} className="flex gap-2">
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Szukaj kategorii po niemiecku, np. Kabelkanal, Wellrohr, Abzweigdose…" leftIcon={<Search className="w-4 h-4" />} autoFocus />
            <Button type="submit" loading={busy}>Szukaj</Button>
          </form>
          {results.length > 0 && (
            <div className="max-h-80 overflow-y-auto rounded-lg border border-ink-200 dark:border-ink-700 divide-y divide-ink-100 dark:divide-ink-800">
              {results.map(c => (
                <button key={c.id} type="button" disabled={saving || !c.leaf} onClick={() => pick(c)} className={cn('w-full text-left px-3 py-2.5 hover:bg-brand-50/60 dark:hover:bg-brand-900/20 disabled:opacity-50 disabled:cursor-not-allowed', !c.leaf && 'opacity-60')}>
                  <div className="text-sm font-semibold text-ink-900 dark:text-ink-50">{c.name} <span className="text-2xs font-normal text-ink-400">#{c.id}{!c.leaf ? ' · nie jest liściem' : ''}</span></div>
                  <div className="text-xs text-ink-500 dark:text-ink-400">{c.path}</div>
                </button>
              ))}
            </div>
          )}
          <p className="text-2xs text-ink-400 dark:text-ink-500">Wybór zapisuje mapę „węzeł sklepu → kategoria" dla klienta. Wszystkie produkty z tego węzła dostaną ją przy następnym biegu; ten wiersz zmieni status, gdy silnik przeliczy zlecenie.</p>
        </CardBody>
      </Card>
    </div>
  );
}

/**
 * T20 „Publikuj" — uruchamia taski czatu 1 (`publish-listings` / `publish-status`) przez edge `publish-order`.
 * Sucha próba = pełny ładunek zapisany w `listing_events`, zero wysyłki. Prawdziwy zapis: admin albo właściciel
 * organizacji klienta, tylko gdy kanał zlecenia ma „Zapis na żywo” (v9/A4, channels.live_writes), etapami 1 → 20 → reszta.
 */
const EVENT_LABEL = {
  product_data_dry_run: { label: 'sucha próba', tone: 'info' },
  product_data_put: { label: 'wysłano', tone: 'success' },
  product_data_rejected: { label: 'odrzucone', tone: 'danger' },
  product_data_blocked: { label: 'zablokowane', tone: 'warning' },
  sellasist_link_dry_run: { label: 'Sellasist: plan powiązania oferty', tone: 'info' },
  sellasist_link_linked: { label: 'Sellasist: oferta powiązana', tone: 'success' },
  sellasist_link_exists: { label: 'Sellasist: powiązanie już było', tone: 'info' },
  sellasist_link_skipped: { label: 'Sellasist: bez powiązania', tone: 'warning' },
  sellasist_link_failed: { label: 'Sellasist: błąd powiązania', tone: 'danger' },
  rejection_autofix: { label: 'poprawka AI po odrzuceniu', tone: 'info' },
  attributes_autofix: { label: 'uzupełnienie parametrów', tone: 'info' },
};

/**
 * Gotowość 21.10 (przebudowa po teście Maćka): integrator KANAŁU zlecenia decyduje, jaka karta zapisu się pokazuje.
 * Kanał przez integratora (settings.via_integrator = 'baselinker' | 'sellasist'; stare dane: true → jedyne połączenie
 * integratora klienta) → jedna karta „Zapis do BASE” albo „Zapis do Sellasist”. Kanał bez integratora z połączeniem
 * Kaufland klienta → karta „Publikacja na Kaufland”. Nigdy obie.
 */
/** Kanały i połączenia klienta zlecenia (do wyboru karty zapisu i do grupy kanałów z jednego wgrania). */
function useOrderSetup(order) {
  const [setup, setSetup] = useState(null);
  const tenantId = order?.tenant_id || null;
  const channelId = Array.isArray(order?.channels) ? order.channels[0] || null : null;
  useEffect(() => {
    let alive = true;
    setSetup(null);
    if (!tenantId) { setSetup({ channels: [], conns: [], channel: null, integrator: null }); return undefined; }
    Promise.all([
      supabase.from('channels').select('id, platform, market, settings, connection_id').eq('tenant_id', tenantId),
      supabase.from('connections').select('id, system, status, meta').eq('tenant_id', tenantId),
    ]).then(([ch, cn]) => {
      if (!alive) return;
      const channels = ch.data || [];
      const conns = cn.data || [];
      const channel = channels.find(c => c.id === channelId) || null;
      setSetup({ channels, conns, channel, integrator: channelIntegrator(channel, conns) });
    });
    return () => { alive = false; };
  }, [tenantId, channelId]);
  return setup;
}

/**
 * v9/C1: zlecenia z TEGO SAMEGO wgrania (kilka kanałów jednego klienta): wspólny `config.upload_id`.
 * Zwraca pozostałe zakończone zlecenia z innym kanałem.
 */
function siblingOrders(order, orders) {
  // Audyt 16.10: tylko wspólny upload_id (jedno wgranie na kilka kanałów). Bez zgadywania po pliku i czasie —
  // ponowne wgranie tego samego pliku na ten sam kanał nie może trafić do jednego zapisu.
  const up = order?.config?.upload_id;
  if (!order?.tenant_id || !up || !Array.isArray(orders)) return [];
  const myChannel = Array.isArray(order.channels) ? order.channels[0] : null;
  const seen = new Set([myChannel]);
  return orders.filter(o => {
    if (o.id === order.id || o.tenant_id !== order.tenant_id || o.status !== 'done' || o.config?.upload_id !== up) return false;
    const ch = Array.isArray(o.channels) ? o.channels[0] : null;
    if (!ch || seen.has(ch)) return false;
    seen.add(ch);
    return true;
  });
}

const INTEGRATORS = {
  baselinker: {
    short: 'BASE', title: 'Zapis do BASE (BaseLinker)', action: 'base_export', prefix: 'base_export_', group: true, outcomes: ['dry_run', 'put', 'blocked', 'error'], putKind: 'put',
    desc: 'Zapisuje gotową treść (tytuł, opis, parametry, kategoria) do katalogu BaseLinker klienta. Ofertę wystawia potem BaseLinker.',
    confirm: 'Istniejące produkty (po EAN) zostaną zaktualizowane, brakujące — dodane.',
  },
  sellasist: {
    short: 'Sellasist', title: 'Zapis do Sellasist', action: 'sellasist_export', prefix: 'sellasist_export_', group: false, outcomes: ['dry_run', 'put', 'blocked', 'error'], putKind: 'put',
    desc: 'Zapisuje tytuł, opis, cechy i producenta (GPSR) do produktów w Sellasist klienta. Ofertę wystawia potem Sellasist.',
    confirm: 'Nadpiszemy tytuł i „Długi opis”. Kopia poprzedniej treści zostaje zachowana.',
  },
};
/**
 * INT-ALLEGRO (22.10): kanał Allegro BEZ integratora (połączenie Allegro klienta) → karta „Publikacja na Allegro”.
 * Wynik per produkt w kolumnie „Allegro” (zdarzenia allegro_dry_run / allegro_offer_created / allegro_blocked / allegro_error).
 */
const ALLEGRO_CFG = {
  short: 'Allegro', title: 'Publikacja na Allegro', action: 'allegro_publish', prefix: 'allegro_', group: false,
  outcomes: ['dry_run', 'offer_created', 'blocked', 'error'], putKind: 'offer_created', putLabel: 'Szkic na Allegro',
};
/**
 * INT-AMAZON (23.10): kanał Amazon BEZ integratora → karta „Publikacja na Amazon”. Sucha próba = walidacja Amazona
 * `mode=VALIDATION_PREVIEW` (nic nie powstaje na koncie). Wynik w kolumnie „Amazon” (amazon_dry_run = VALID,
 * amazon_blocked = INVALID / braki z powodami, amazon_error). Zapisu na żywo nie ma — `config.amazon_writes_enabled` = OFF.
 */
const AMAZON_CFG = {
  short: 'Amazon', title: 'Publikacja na Amazon', action: 'amazon_publish', prefix: 'amazon_', group: false,
  outcomes: ['dry_run', 'offer_created', 'blocked', 'error'], putKind: 'offer_created', putLabel: 'Oferta na Amazon',
};
/**
 * Uwagi Maćka 21.10 (pkt „wynik jako status w tabeli”): Kaufland bezpośrednio — wynik suchej próby / wysyłki karty
 * (product_data_dry_run / _put / _blocked / _rejected z publish-listings) w kolumnie „Kaufland” tabeli Produkty,
 * z wykrzyknikiem przy blokadzie albo odrzuceniu. Dziennik publikacji w karcie zostaje (pełny ładunek).
 */
const KAUFLAND_CFG = {
  short: 'Kaufland', title: 'Publikacja na Kaufland', action: 'publish', prefix: 'product_data_', group: false,
  // Audyt 24.10: `product_data_status` (sprawdzenie statusu karty po wysyłce) — odrzucenie później (update_fail_reason) → „!”,
  // product_ready → „żywe”. Do tego status oferty `rejected` z tabeli listings (kolumna pokazuje „!” także bez zdarzenia).
  outcomes: ['dry_run', 'put', 'blocked', 'rejected', 'status'], putKind: 'put', putLabel: 'Wysłano do Kauflanda', rowRejected: true,
};
function readyForIntegrator(integrator, rows) {
  return rows.filter(r => r.status === 'ready' || r.status === 'live'
    || ((integrator === 'sellasist' || integrator === 'allegro' || integrator === 'amazon') && r.status === 'review' && r.reasons.length > 0 && r.reasons.every(x => x.code === 'checks_unavailable')));
}

const channelLabel = (ch) => (ch ? `${PLATFORM_LABEL[ch.platform] || ch.platform} ${String(ch.market || '').toUpperCase()}`.trim() : '');

/**
 * Jedna karta zapisu do integratora klienta (BASE albo Sellasist). Wynik zapisu NIE ma osobnego dziennika —
 * pokazuje się przy każdym produkcie w tabeli Produkty (kolumna BASE / Sellasist).
 */
function IntegratorCard({ order, rows, isAdmin, integrator, siblings = [], channelsById = {}, onStarted, progress = null, onRefresh }) {
  const cfg = INTEGRATORS[integrator];
  const [busy, setBusy] = useState(null);
  const [ask, setAsk] = useState(false);
  const [together, setTogether] = useState(true);
  const [writesList, setWritesList] = useState(null);
  const [writesErr, setWritesErr] = useState(null);
  const [toggling, setToggling] = useState(false);
  const groupSiblings = cfg.group ? siblings : [];
  const scopeKey = (together && groupSiblings.length ? [order.id, ...groupSiblings.map(o => o.id)] : [order.id]).join(',');
  const isGroup = scopeKey.includes(',');

  const loadWrites = useCallback(async () => {
    setWritesErr(null);
    try { setWritesList(await loadWritesInfo(scopeKey.split(','))); }
    catch (e) { setWritesList(null); setWritesErr(e.message || 'błąd'); }
  }, [scopeKey]);
  useEffect(() => { setWritesList(null); loadWrites(); }, [loadWrites]);
  const writes = aggregateWrites(writesList);
  const canWrite = isAdmin || !!writes?.can_toggle;

  // Przełącznik obejmuje WSZYSTKIE kanały zakresu (bieżący + pozostałe z wgrania, gdy „Razem” zaznaczone).
  const setLive = async (enabled) => {
    setToggling(true);
    // Optymistycznie: przełącznik zmienia się od razu, edge potwierdza w tle (wcześniej 3–4 s czekania).
    setWritesList(prev => (prev || []).map(w => (w ? { ...w, writes_enabled: enabled } : w)));
    try {
      const list = await Promise.all(scopeKey.split(',').map(id => callEdge(PUBLISH_ORDER_URL, { order_id: id, action: 'set_writes', enabled })));
      setWritesList(list);
      toast.success(enabled ? `Zapis na konto klienta włączony${isGroup ? ` we wszystkich kanałach (${list.length})` : ''}.` : `Zapis wyłączony${isGroup ? ` we wszystkich kanałach (${list.length})` : ''} — działa tylko sucha próba.`);
    } catch (e) {
      toast.error(e.message);
      loadWrites();
    } finally { setToggling(false); }
  };

  const ready = readyForIntegrator(integrator, rows);
  const run = async (dryRun) => {
    setBusy(dryRun ? 'dry' : 'write'); setAsk(false);
    const group = isGroup ? scopeKey.split(',') : null;
    try {
      await callEdge(PUBLISH_ORDER_URL, { order_id: order.id, action: cfg.action, dry_run: dryRun, ...(group ? { order_ids: group } : {}) });
      toast.success(`${dryRun ? `Sucha próba ${cfg.short} uruchomiona` : `Zapis do ${cfg.short} uruchomiony`}${group ? ` (${group.length} kanały z tego wgrania)` : ''}. Wynik pojawi się w tabeli Produkty, w kolumnie ${cfg.short}.`);
      onStarted?.(dryRun);
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const siblingNames = groupSiblings.map(o => channelLabel(channelsById[Array.isArray(o.channels) ? o.channels[0] : '']) || `${o.marketplace || ''} ${o.language || ''}`.trim()).join(', ');

  // 23.10 (BL-DEEP 10c, za przełącznikiem `tenants.rules.gpsr_client_list` / `config.gpsr_client_list_enabled`):
  // „Dane producentów do GPSR” — XLSX dla klienta per zlecenie (producent z pliku → słownik → propozycja agenta).
  // API BaseLinkera nie ma pól GPSR, więc klient uzupełnia je w panelu BL / na etykiecie sam; ta lista mówi co i skąd.
  const downloadGpsr = async () => {
    setBusy('gpsr');
    try {
      const r = await callEdge(PUBLISH_ORDER_URL, { order_id: order.id, action: 'gpsr_list' });
      if (!r?.enabled) { toast.info('Lista GPSR jest wyłączona dla tego klienta.'); return; }
      if (!r.rows?.length) { toast.info('W pliku tego zlecenia nie ma kolumny producenta / marki — lista GPSR jest pusta.'); return; }
      const wb = XLSX.utils.book_new();
      const intro = [
        ['Dane producentów do GPSR', order.tenant_name || ''],
        ['Zlecenie', order.id, order.filename || ''],
        ['Wygenerowano', new Date().toLocaleString('pl-PL')],
        ['Producentów', r.counts.producers, 'gotowe', r.counts.ready, 'do potwierdzenia', r.counts.to_confirm, 'do uzupełnienia', r.counts.missing],
        ['Co z tym zrobić', 'Dane „gotowe” wpisz w BaseLinkerze (Produkty → Producenci → pola GPSR) albo na etykietę; „do potwierdzenia” sprawdź u producenta/w KRS; „do uzupełnienia” — brakujące pola.'],
      ];
      const ws1 = XLSX.utils.aoa_to_sheet(intro);
      XLSX.utils.book_append_sheet(wb, ws1, 'Jak czytać');
      const rows = r.rows.map(x => ({
        'Producent (z pliku)': x.name, 'Nazwa prawna': x.legal_name || '', 'Adres': x.address || '', 'E-mail': x.email || '', 'Telefon': x.phone || '', 'Formularz kontaktowy': x.form_url || '', 'Kraj': x.country || '',
        'Nr rejestru': x.register_id || '', 'Rola': x.role === 'distributor' ? 'dystrybutor' : x.role === 'manufacturer' ? 'producent' : '', 'Status': x.status, 'Pewność': x.confidence || '',
        'Źródło': x.source, 'Link źródła': x.source_url || '', 'Do sprawdzenia': x.todo || '', 'Produktów w zleceniu': x.products, 'Przykładowe SKU': x.example_skus,
      }));
      const ws2 = XLSX.utils.json_to_sheet(rows);
      ws2['!cols'] = [22, 34, 36, 28, 14, 30, 6, 18, 12, 16, 10, 30, 40, 50, 10, 30].map(w => ({ wch: w }));
      XLSX.utils.book_append_sheet(wb, ws2, 'Producenci');
      XLSX.writeFile(wb, `GPSR_producenci_${(order.tenant_name || 'klient').replace(/[^\p{L}\p{N}_-]+/gu, '_')}_${order.id.slice(0, 8)}.xlsx`);
      toast.success(`Lista GPSR: ${r.counts.producers} producentów (gotowe ${r.counts.ready}, do potwierdzenia ${r.counts.to_confirm}, do uzupełnienia ${r.counts.missing}).`);
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  return (
    <Card className="mb-5">
      <CardHeader>
        <CardTitle>{cfg.title}</CardTitle>
        <CardDescription>{cfg.desc} Gotowych produktów: {formatNumber(ready.length)}.</CardDescription>
      </CardHeader>
      <CardBody className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="secondary" loading={busy === 'dry'} disabled={!!busy || !!progress || !ready.length} onClick={() => run(true)}>Sucha próba {cfg.short}</Button>
        {canWrite && <Button size="sm" loading={busy === 'write'} disabled={!!busy || !!progress || !ready.length || !writes?.writes_enabled} onClick={() => setAsk(true)}>Zapisz do {cfg.short}</Button>}
        {integrator === 'baselinker' && writes?.gpsr_list_enabled && <Button size="sm" variant="ghost" loading={busy === 'gpsr'} disabled={!!busy} onClick={downloadGpsr} title="XLSX: producenci z tego zlecenia z danymi do rejestru GPSR (słownik klienta + propozycje agenta). Nic nie zapisuje.">Dane producentów do GPSR (XLSX)</Button>}
        {onRefresh && <Button size="sm" variant="ghost" onClick={onRefresh} leftIcon={<RefreshCw className="w-3.5 h-3.5" />}>Odśwież</Button>}
        {progress ? <ExportProgress progress={progress} short={cfg.short} className="flex-1 min-w-[12rem]" /> : (
          <span className="text-2xs text-ink-500 dark:text-ink-400 flex-1 min-w-[12rem]">Najpierw sucha próba. Wynik widać przy każdym produkcie w tabeli poniżej.</span>
        )}
        <WritesSwitch writes={writes} error={writesErr} onRetry={loadWrites} busy={toggling} onChange={setLive} count={isGroup ? scopeKey.split(',').length : 1} />
        {groupSiblings.length > 0 && (
          <label className="w-full flex items-center gap-2 text-xs text-ink-600 dark:text-ink-300">
            <input type="checkbox" checked={together} onChange={e => setTogether(e.target.checked)} />
            Razem z pozostałymi kanałami z tego wgrania ({siblingNames}) — jeden zapis na produkt. Przełącznik zapisu obejmuje wszystkie te kanały.
          </label>
        )}
      </CardBody>
      {ask && (
        <div className="fixed inset-0 z-50 bg-ink-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setAsk(false)}>
          <div className="bg-white dark:bg-ink-900 rounded-xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="px-6 pt-5 pb-4">
              <div className="text-base font-semibold text-ink-900 dark:text-ink-50">{cfg.title}</div>
              <p className="mt-2 text-sm text-ink-600 dark:text-ink-300">Zapiszemy treść <b>{formatNumber(ready.length)}</b> produktów{isGroup ? ' (razem z pozostałymi kanałami z tego wgrania)' : ''} na konto klienta{order.tenant_name ? <> <b>{order.tenant_name}</b></> : ''}. {cfg.confirm}</p>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-ink-100 dark:border-ink-800">
              <Button variant="ghost" onClick={() => setAsk(false)}>Anuluj</Button>
              <Button onClick={() => run(false)}>Zapisz do {cfg.short}</Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

/**
 * INT-ALLEGRO (22.10): „Publikacja na Allegro” — klient podpięty bezpośrednio do Allegro (połączenie przez device flow).
 * Sucha próba (ładunek w dzienniku, nic nie idzie do Allegro) i „Wystaw szkice (nieaktywne)” — oferty z produktu katalogowego
 * po EAN, ZAWSZE jako szkic (INACTIVE). Zapis tylko z włączonym „Zapisem na konto klienta”; produkcja Allegro = tylko odczyt.
 */
function AllegroCard({ order, rows, isAdmin, conn, onStarted, progress = null, onRefresh, onConnectAccount }) {
  const [busy, setBusy] = useState(null);
  const [ask, setAsk] = useState(false);
  const [scope, setScope] = useState('one'); // 'one' = 1 produkt (test), 'all' = wszystkie gotowe
  const [writes, setWrites] = useState(null);
  const [toggling, setToggling] = useState(false);
  const env = conn?.meta?.env === 'prod' ? 'prod' : 'sandbox';
  const readOnly = env === 'prod' && conn?.meta?.read_only !== false;
  const connOk = conn?.status === 'ok';

  const [writesErr, setWritesErr] = useState(null);
  const loadWrites = useCallback(async () => {
    setWritesErr(null);
    try { setWrites((await loadWritesInfo([order.id]))[0]); } catch (e) { setWrites(null); setWritesErr(e.message || 'błąd'); }
  }, [order.id]);
  useEffect(() => { loadWrites(); }, [loadWrites]);
  const setLive = async (enabled) => {
    setToggling(true);
    setWrites(w => (w ? { ...w, writes_enabled: enabled } : w)); // optymistycznie
    try {
      setWrites(await callEdge(PUBLISH_ORDER_URL, { order_id: order.id, action: 'set_writes', enabled }));
      toast.success(enabled ? 'Zapis na konto Allegro klienta włączony' : 'Zapis wyłączony — działa tylko sucha próba');
    } catch (e) { toast.error(e.message); loadWrites(); } finally { setToggling(false); }
  };
  const canWrite = isAdmin || !!writes?.can_toggle;

  const ready = readyForIntegrator('allegro', rows);
  const withOffer = rows.filter(r => r.platform_offer_id);
  const pending = ready.filter(r => !r.platform_offer_id);
  const scoped = scope === 'one' ? pending.slice(0, 1) : pending;
  const run = async (dryRun) => {
    const list = dryRun ? (scope === 'one' ? ready.slice(0, 1) : ready) : scoped;
    if (!list.length) return;
    setBusy(dryRun ? 'dry' : 'write'); setAsk(false);
    try {
      await callEdge(PUBLISH_ORDER_URL, { order_id: order.id, action: 'allegro_publish', dry_run: dryRun, row_indexes: list.map(r => r.row_index).filter(x => x != null) });
      toast.success(`${dryRun ? 'Sucha próba Allegro uruchomiona' : `Wystawianie szkiców uruchomione (${list.length} szt.)`}. Wynik pojawi się w tabeli Produkty, w kolumnie Allegro.`);
      onStarted?.(dryRun, list.length, list.map(r => r.row_index).filter(x => x != null));
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  return (
    <Card className="mb-5">
      <CardHeader className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <CardTitle>{ALLEGRO_CFG.title}</CardTitle>
          <CardDescription>
            Oferta z produktu katalogowego Allegro (po EAN) z tytułem i opisem z MAMS — zawsze jako <b>szkic (nieaktywna)</b>; aktywujesz ją na Allegro.
            {' '}Gotowych: {formatNumber(ready.length)} · ze szkicem na Allegro: {formatNumber(withOffer.length)}.
          </CardDescription>
        </div>
        {conn && (
          <div className="flex items-center gap-2">
            <Badge variant={env === 'prod' ? 'danger' : 'info'} size="sm">{env === 'prod' ? 'Allegro · produkcja' : 'Allegro · sandbox'}</Badge>
            {conn.meta?.login && <span className="text-xs text-ink-500 dark:text-ink-400">konto: <b>{conn.meta.login}</b></span>}
          </div>
        )}
      </CardHeader>
      <CardBody className="grid gap-3">
        {!conn && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200 px-4 py-3 text-sm flex flex-wrap items-center gap-3">
            <span className="flex-1 min-w-[16rem]">Klient <b>{order.tenant_name || ''}</b> nie ma jeszcze połączenia z Allegro. Podłącz konto: <b>Klienci → ten klient → Allegro → Połącz</b> (logowanie na konto Allegro klienta i wpisanie kodu).</span>
            {onConnectAccount && <Button size="sm" onClick={onConnectAccount}>Podłącz konto Allegro</Button>}
          </div>
        )}
        {conn && !connOk && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200 px-4 py-3 text-sm">
            Połączenie Allegro klienta ma status <b>{conn.status}</b>{conn.last_error ? ` (${conn.last_error})` : ''} — połącz konto ponownie w zakładce Klienci.
          </div>
        )}
        {readOnly && conn && (
          <div className="rounded-lg border border-ink-200 bg-ink-50 text-ink-700 dark:border-ink-700 dark:bg-ink-800/40 dark:text-ink-200 px-4 py-2 text-xs">
            Konto produkcyjne Allegro jest w trybie <b>tylko odczyt</b> — działa sucha próba. Wystawianie na prawdziwym koncie włącza właściciel MAMS po zgodzie klienta.
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <select value={scope} onChange={e => setScope(e.target.value)} className="h-8 rounded-md border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 text-xs px-2" title="Zakres: oferty powstaną TYLKO dla wybranych wierszy">
            <option value="one">1 produkt (test)</option>
            <option value="all">wszystkie gotowe ({formatNumber(ready.length)})</option>
          </select>
          <Button size="sm" variant="secondary" loading={busy === 'dry'} disabled={!!busy || !!progress || !connOk || !ready.length} onClick={() => run(true)}>Sucha próba</Button>
          {canWrite && !readOnly && <Button size="sm" loading={busy === 'write'} disabled={!!busy || !!progress || !connOk || !scoped.length || !writes?.writes_enabled} onClick={() => setAsk(true)} title={!writes?.writes_enabled ? 'Najpierw włącz „Zapis na konto klienta”' : !scoped.length ? 'Wszystkie gotowe produkty mają już szkic na Allegro' : ''}>Wystaw szkice (nieaktywne)</Button>}
          {onRefresh && <Button size="sm" variant="ghost" onClick={onRefresh} leftIcon={<RefreshCw className="w-3.5 h-3.5" />}>Odśwież</Button>}
          {progress ? <ExportProgress progress={progress} short="Allegro" className="flex-1 min-w-[12rem]" /> : (
            <span className="text-2xs text-ink-500 dark:text-ink-400 flex-1 min-w-[12rem]">Najpierw sucha próba. Wynik i powody blokad przy każdym produkcie w tabeli poniżej.</span>
          )}
          {!readOnly && <WritesSwitch writes={writes} error={writesErr} onRetry={loadWrites} busy={toggling} onChange={setLive} />}
        </div>
      </CardBody>
      {ask && (
        <div className="fixed inset-0 z-50 bg-ink-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setAsk(false)}>
          <div className="bg-white dark:bg-ink-900 rounded-xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="px-6 pt-5 pb-4">
              <div className="text-base font-semibold text-ink-900 dark:text-ink-50">Wystaw szkice na Allegro</div>
              <p className="mt-2 text-sm text-ink-600 dark:text-ink-300">Utworzymy <b>{formatNumber(scoped.length)}</b> {scoped.length === 1 ? 'ofertę' : 'oferty'} jako <b>szkic (nieaktywne)</b> na koncie Allegro{env === 'prod' ? '' : ' (sandbox)'}{conn?.meta?.login ? <> <b>{conn.meta.login}</b></> : ''}. Nic nie będzie widoczne dla kupujących, dopóki ktoś nie aktywuje oferty na Allegro.</p>
              <p className="mt-2 text-xs text-ink-500 dark:text-ink-400">Produkty z blokadą (brak produktu w katalogu, brak ceny, wymagany parametr) zostaną pominięte z powodem.</p>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-ink-100 dark:border-ink-800">
              <Button variant="ghost" onClick={() => setAsk(false)}>Anuluj</Button>
              <Button onClick={() => run(false)}>Wystaw {formatNumber(scoped.length)} {scoped.length === 1 ? 'szkic' : 'szkice'}</Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

/**
 * INT-AMAZON (23.10): „Publikacja na Amazon” — sucha próba = walidacja Amazona (Listings Items, mode=VALIDATION_PREVIEW):
 * EAN → ASIN (oferta do istniejącej karty) albo pełna karta nowego produktu; Amazon ocenia ładunek, nic nie powstaje.
 * Przycisk wystawienia jest wyłączony, dopóki właściciel MAMS nie włączy `amazon_writes_enabled` (decyzja + konto klienta).
 */
function AmazonCard({ order, rows, conn, channel, onStarted, progress = null, onRefresh, onConnectAccount }) {
  const [busy, setBusy] = useState(false);
  const [scope, setScope] = useState('three');
  const mediafy = channel?.settings?.amazon?.account === 'mediafy';
  const connOk = conn?.status === 'ok' || (!conn && mediafy);
  const ready = readyForIntegrator('amazon', rows);
  const pool = ready.length ? ready : rows.filter(r => r.status !== 'failed');
  const list = scope === 'one' ? pool.slice(0, 1) : scope === 'three' ? pool.slice(0, 3) : pool;
  const run = async () => {
    if (!list.length) return;
    setBusy(true);
    try {
      await callEdge(PUBLISH_ORDER_URL, { order_id: order.id, action: 'amazon_publish', dry_run: true, row_indexes: list.map(r => r.row_index).filter(x => x != null) });
      toast.success('Sucha próba Amazon uruchomiona (walidacja Amazona bez zapisu). Wynik pojawi się w tabeli Produkty, w kolumnie Amazon.');
      onStarted?.(true, list.length, list.map(r => r.row_index).filter(x => x != null));
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const marketLabel = String(channel?.market || 'de').toUpperCase();
  return (
    <Card className="mb-5">
      <CardHeader className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <CardTitle>{AMAZON_CFG.title}</CardTitle>
          <CardDescription>
            EAN → katalog Amazon {marketLabel}: produkt istnieje (ASIN) → <b>oferta do istniejącej karty</b>; nie istnieje → <b>nowa karta</b> z tytułem, punktami i opisem z MAMS.
            {' '}Sucha próba to walidacja Amazona bez zapisu — nic nie powstaje na koncie. Produktów: {formatNumber(pool.length)}.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="info" size="sm">{`Amazon · ${marketLabel}`}</Badge>
          {conn?.meta?.store_name && <span className="text-xs text-ink-500 dark:text-ink-400">konto: <b>{conn.meta.store_name}</b></span>}
          {!conn && mediafy && <span className="text-xs text-ink-500 dark:text-ink-400">konto: <b>Mediafy (test)</b></span>}
        </div>
      </CardHeader>
      <CardBody className="grid gap-3">
        {!conn && !mediafy && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200 px-4 py-3 text-sm flex flex-wrap items-center gap-3">
            <span className="flex-1 min-w-[16rem]">Klient <b>{order.tenant_name || ''}</b> nie ma jeszcze połączenia z Amazon. Podłącz konto: <b>Klienci → ten klient → Amazon</b> (Refresh Token i Seller ID z autoryzacji aplikacji MAMS).</span>
            {onConnectAccount && <Button size="sm" onClick={onConnectAccount}>Podłącz konto Amazon</Button>}
          </div>
        )}
        {conn && conn.status !== 'ok' && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200 px-4 py-3 text-sm">
            Połączenie Amazon klienta ma status <b>{conn.status}</b>{conn.last_error ? ` (${conn.last_error})` : ''} — przetestuj je w zakładce Klienci.
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <select value={scope} onChange={e => setScope(e.target.value)} className="h-8 rounded-md border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 text-xs px-2" title="Zakres walidacji">
            <option value="one">1 produkt</option>
            <option value="three">3 produkty (test)</option>
            <option value="all">wszystkie ({formatNumber(pool.length)})</option>
          </select>
          <Button size="sm" variant="secondary" loading={busy} disabled={busy || !!progress || !connOk || !list.length} onClick={run}>Sucha próba</Button>
          <Button size="sm" disabled title="Wystawianie na Amazon jest wyłączone (amazon_writes_enabled = OFF). Pierwsza oferta na żywo — decyzja właściciela MAMS.">Wystaw na Amazon</Button>
          {onRefresh && <Button size="sm" variant="ghost" onClick={onRefresh} leftIcon={<RefreshCw className="w-3.5 h-3.5" />}>Odśwież</Button>}
          {progress ? <ExportProgress progress={progress} short="Amazon" className="flex-1 min-w-[12rem]" /> : (
            <span className="text-2xs text-ink-500 dark:text-ink-400 flex-1 min-w-[12rem]">Wynik i błędy Amazona (z nazwą pola) przy każdym produkcie w tabeli poniżej.</span>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

/** „Trwa… (x/y produktów)” — postęp suchej próby / zapisu do integratora. */
function ExportProgress({ progress, short, className }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium text-brand-700 dark:text-brand-300', className)}>
      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
      {progress.dry ? 'Trwa sucha próba' : short === 'Allegro' ? 'Trwa wystawianie szkiców na Allegro' : `Trwa zapis do ${short}`}… ({formatNumber(progress.done)}/{formatNumber(progress.total)} produktów)
    </span>
  );
}

/**
 * Ostatnie zdarzenie zapisu do integratora per produkt (listing_events z prefiksem base_export_ / sellasist_export_).
 * Zwraca { [listing_id]: { last, lastPut } }.
 */
const EXPORT_OUTCOMES = ['dry_run', 'put', 'blocked', 'error'];
function useExportStatus(rows, prefix, orderId, outcomes = EXPORT_OUTCOMES, putKind = 'put') {
  const outcomesKey = outcomes.join(',');
  const [byListing, setByListing] = useState(null);
  const seq = useRef(0);
  const load = useCallback(async () => {
    const my = ++seq.current;
    if (!prefix || !rows?.length) { setByListing({}); return; }
    const ids = rows.map(r => r.id);
    const types = outcomesKey.split(',').map(x => prefix + x);
    const chunks = [];
    for (let i = 0; i < ids.length; i += 50) chunks.push(ids.slice(i, i + 50));
    const out = {};
    for (let i = 0; i < chunks.length; i += 6) {
      const res = await Promise.all(chunks.slice(i, i + 6).map(c => supabase.from('listing_events')
        .select('id, listing_id, created_at, event_type, response').in('listing_id', c).in('event_type', types)
        .order('created_at', { ascending: false }).limit(1000)));
      res.forEach(({ data }) => (data || []).forEach(ev => {
        const cur = out[ev.listing_id] || (out[ev.listing_id] = { last: ev, lastPut: null });
        if (!cur.lastPut && ev.event_type === `${prefix}${putKind}`) cur.lastPut = ev;
      }));
    }
    if (my === seq.current) setByListing(out);
  }, [rows, prefix, outcomesKey, putKind]);
  useEffect(() => { load(); }, [load]);

  // Odpytywanie po suchej próbie / zapisie: JEDNO lekkie zapytanie o zdarzenia zlecenia nowsze niż `since`
  // (czas serwera z ostatniego znanego zdarzenia) — dopisywane do stanu bez przeładowania tabeli.
  const loadSince = useCallback(async (since) => {
    if (!prefix || !orderId) return;
    ++seq.current; // starsze pełne odczyty nie nadpiszą świeższych zdarzeń
    let q = supabase.from('listing_events')
      .select('id, listing_id, created_at, event_type, response, listings!inner(order_id)')
      .eq('listings.order_id', orderId).in('event_type', outcomesKey.split(',').map(x => prefix + x));
    if (since) q = q.gt('created_at', since);
    const { data, error } = await q.order('created_at', { ascending: true }).limit(2000);
    if (error) { load(); return; } // awaryjnie: pełny odczyt po id produktów
    if (!data?.length) return;
    setByListing(prev => {
      const next = { ...(prev || {}) };
      data.forEach(({ listings: _l, ...ev }) => {
        const cur = next[ev.listing_id] ? { ...next[ev.listing_id] } : { last: null, lastPut: null };
        if (!cur.last || ev.created_at >= cur.last.created_at) cur.last = ev;
        if (ev.event_type === `${prefix}${putKind}` && (!cur.lastPut || ev.created_at >= cur.lastPut.created_at)) cur.lastPut = ev;
        next[ev.listing_id] = cur;
      });
      return next;
    });
  }, [prefix, orderId, load, outcomesKey, putKind]);
  return [byListing, load, loadSince];
}

/** Najnowszy znany czas zdarzenia (czas serwera) — punkt odniesienia „co nowego od kliknięcia”. */
function latestEventAt(byListing) {
  let max = null;
  Object.values(byListing || {}).forEach(x => { if (x?.last && (!max || x.last.created_at > max)) max = x.last.created_at; });
  return max;
}

function exportWarnings(ev) {
  const r = ev?.response || {};
  return [...(Array.isArray(r.warnings) ? r.warnings : []), ...(Array.isArray(r.gpsrProblems) ? r.gpsrProblems : []), ...(Array.isArray(r.gpsrWarnings) ? r.gpsrWarnings : [])].map(String);
}
/** Tekst do xlsx: wynik ostatniego zdarzenia zapisu do integratora. */
function exportText(info, cfg) {
  if (!info) return '';
  const ev = info.last;
  const kind = exportKind(ev, cfg);
  const at = formatDateTime(ev.created_at);
  if (kind === (cfg.putKind || 'put')) return cfg.putLabel ? `${cfg.putLabel.toLowerCase()} ${ev.response?.offerId || ''} ${at}`.replace(/\s+/g, ' ') : `zapisano ${at}`;
  if (kind === 'dry_run') return `sucha próba ${at}`;
  if (kind === 'live') return `żywe ${at}`;
  return `${kind === 'blocked' ? 'zablokowane' : kind === 'rejected' ? 'odrzucone' : 'błąd'} ${at}: ${exportProblems(ev).join('; ')}`;
}
const shortTime = (d) => new Date(d).toLocaleString('pl-PL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/** Komórka tabeli: wynik ostatniej suchej próby / zapisu do integratora dla produktu. */
function ExportCell({ info, cfg, onOpen, rowStatus }) {
  // Audyt 24.10: oferta odrzucona później (status w listings) — „!” nawet bez zdarzenia odrzucenia w dzienniku.
  if (cfg.rowRejected && rowStatus === 'rejected' && exportKind(info?.last, cfg) !== 'rejected') {
    return (
      <button type="button" onClick={info ? onOpen : undefined} title={`Odrzucone przez ${cfg.short} (status oferty)${info?.last ? ` · ostatnie zdarzenie: ${formatDateTime(info.last.created_at)}` : ''}`}
        className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-danger text-white text-sm font-bold hover:opacity-90">!</button>
    );
  }
  if (!info) return <span className="text-ink-300 dark:text-ink-600">—</span>;
  const ev = info.last;
  const kind = exportKind(ev, cfg);
  if (kind === 'live') {
    return (
      <button type="button" onClick={onOpen} title={`Karta żywa na ${cfg.short} (product_ready): ${formatDateTime(ev.created_at)}`} className="text-left">
        <Badge variant="success" size="sm">żywe ✓</Badge>
      </button>
    );
  }
  if (kind === 'blocked' || kind === 'error' || kind === 'rejected') {
    const why = exportProblems(ev).join('; ') || (kind === 'blocked' ? 'zapis zablokowany' : kind === 'rejected' ? 'odrzucone przez platformę' : 'błąd zapisu');
    return (
      <button type="button" onClick={onOpen} title={`${kind === 'blocked' ? 'Zablokowane' : kind === 'rejected' ? 'Odrzucone' : 'Błąd'} (${formatDateTime(ev.created_at)}): ${why}`}
        className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-danger text-white text-sm font-bold hover:opacity-90">!</button>
    );
  }
  if (kind === (cfg.putKind || 'put')) {
    const offerId = ev.response?.offerId;
    const url = offerId ? (ev.response?.url || allegroOfferUrl(ev.response?.env, offerId)) : null;
    // 23.10: szkic Allegro powstał, ale z błędami walidacji (nie da się go aktywować) — „!” z powodami zamiast zielonego ✓.
    const notOk = exportProblems(ev);
    if (notOk.length) {
      return (
        <span className="inline-block text-left">
          <button type="button" onClick={onOpen} title={`${cfg.putLabel || 'Zapisano'} z błędami (${formatDateTime(ev.created_at)}): ${notOk.join('; ')}`}
            className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-warning text-white text-sm font-bold hover:opacity-90">!</button>
          {offerId && url && <a href={url} target="_blank" rel="noreferrer" className="block text-2xs text-brand-700 dark:text-brand-300 hover:underline mt-0.5">{offerId}{ev.response?.env === 'prod' ? '' : ' · sandbox'} ↗</a>}
        </span>
      );
    }
    return (
      <span className="inline-block text-left">
        <button type="button" onClick={onOpen} title={`${cfg.putLabel || `Zapisano w ${cfg.short}`}: ${formatDateTime(ev.created_at)}`} className="text-left">
          <Badge variant="success" size="sm">{cfg.putLabel || `Zapisano w ${cfg.short}`} ✓</Badge>
        </button>
        {offerId && url
          ? <a href={url} target="_blank" rel="noreferrer" className="block text-2xs text-brand-700 dark:text-brand-300 hover:underline mt-0.5" title={ev.response?.env === 'prod' ? 'Oferta na Allegro' : 'Oferta w sandboxie Allegro'}>{offerId}{ev.response?.env === 'prod' ? '' : ' · sandbox'} ↗</a>
          : <span className="block text-2xs text-ink-400 mt-0.5">{shortTime(ev.created_at)}</span>}
      </span>
    );
  }
  const warn = exportWarnings(ev);
  return (
    <button type="button" onClick={onOpen} title={`Sucha próba: ${formatDateTime(ev.created_at)}${warn.length ? ` · uwagi: ${warn.join('; ')}` : ''}${info.lastPut ? ` · ostatni zapis: ${formatDateTime(info.lastPut.created_at)}` : ''}`} className="text-left">
      <Badge variant="info" size="sm">Sucha próba ✓</Badge>
      {info.lastPut && <span className="block text-2xs text-success mt-0.5">{cfg.putLabel ? `${cfg.putLabel.toLowerCase()}: ${info.lastPut.response?.offerId || ''}` : `zapisano ${shortTime(info.lastPut.created_at)}`}</span>}
    </button>
  );
}

/** Szczegóły zdarzenia po kliknięciu w komórkę (powód, uwagi, ładunek na żądanie). */
function ExportEventDialog({ info, cfg, sku, onClose }) {
  const ev = info.last;
  const kind = exportKind(ev, cfg);
  const [payload, setPayload] = useState(null);
  const [loadingPayload, setLoadingPayload] = useState(false);
  const problems = exportProblems(ev);
  const warnings = exportWarnings(ev);
  const showPayload = async () => {
    setLoadingPayload(true);
    const { data } = await supabase.from('listing_events').select('request, response').eq('id', ev.id).maybeSingle();
    setPayload(data || {}); setLoadingPayload(false);
  };
  const isPut = kind === (cfg.putKind || 'put');
  const offerId = isPut ? ev.response?.offerId : null;
  const offerUrl = offerId ? (ev.response?.url || allegroOfferUrl(ev.response?.env, offerId)) : null;
  const title = isPut ? (cfg.putLabel ? `${cfg.putLabel} (nieaktywny)` : `Zapisano w ${cfg.short}`) : kind === 'dry_run' ? 'Sucha próba' : kind === 'live' ? 'Karta żywa' : kind === 'rejected' ? 'Odrzucone przez platformę' : kind === 'blocked' ? (ev.response?.review ? 'Do przeglądu' : 'Zapis zablokowany') : 'Błąd zapisu';
  return (
    <div className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <CardHeader className="flex items-start justify-between gap-3">
          <div>
            <CardTitle>{cfg.short} — {sku || 'produkt'}</CardTitle>
            <CardDescription>{title} · {formatDateTime(ev.created_at)}{info.lastPut && !isPut ? ` · ${cfg.putLabel ? `${cfg.putLabel.toLowerCase()} ${info.lastPut.response?.offerId || ''}` : 'ostatni zapis'}: ${formatDateTime(info.lastPut.created_at)}` : ''}</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}><XCircle className="w-4 h-4" /></Button>
        </CardHeader>
        <CardBody className="grid gap-3 text-sm">
          {problems.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200 px-3 py-2">
              <div className="font-semibold mb-1">Powód</div>
              <ul className="grid gap-1 text-xs">{problems.map((p, i) => <li key={i}>• {p}</li>)}</ul>
            </div>
          )}
          {warnings.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200 px-3 py-2">
              <div className="font-semibold mb-1">Uwagi</div>
              <ul className="grid gap-1 text-xs">{warnings.map((p, i) => <li key={i}>• {p}</li>)}</ul>
            </div>
          )}
          {offerId && (
            <p className="text-ink-700 dark:text-ink-200">Oferta <b>{offerId}</b> — szkic (nieaktywna){ev.response?.env === 'prod' ? '' : ' w sandboxie Allegro'}. {offerUrl && <a href={offerUrl} target="_blank" rel="noreferrer" className="text-brand-700 dark:text-brand-300 hover:underline">Otwórz na Allegro ↗</a>}</p>
          )}
          {!problems.length && !warnings.length && <p className="text-ink-600 dark:text-ink-300">{isPut ? 'Zapis przeszedł bez uwag.' : 'Sucha próba bez uwag — tak poleci przy zapisie.'}</p>}
          {payload ? (
            <pre className="text-2xs whitespace-pre-wrap break-all max-h-72 overflow-y-auto rounded-lg bg-ink-50 dark:bg-ink-800/40 p-3">{JSON.stringify(payload, null, 2)}</pre>
          ) : (
            <div><Button size="xs" variant="ghost" loading={loadingPayload} onClick={showPayload}>Pokaż ładunek</Button></div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

// v9/A4: przełącznik dotyczy KANAŁU zlecenia (channels.live_writes); włącza admin albo właściciel organizacji.
// `count` > 1 = przełącznik obejmuje kilka kanałów naraz (stan „włączony” tylko, gdy włączony we wszystkich).
function WritesSwitch({ writes, busy, onChange, count = 1, error = null, onRetry = null }) {
  if (!writes && error) {
    return (
      <span className="inline-flex items-center gap-2 text-xs text-danger" title={error}>
        Nie udało się sprawdzić stanu zapisu
        {onRetry && <Button size="xs" variant="ghost" onClick={onRetry} leftIcon={<RefreshCw className="w-3 h-3" />}>Spróbuj ponownie</Button>}
      </span>
    );
  }
  if (!writes) return <Badge variant="neutral" size="sm">stan zapisu: sprawdzany…</Badge>;
  if (!writes.channel_id) return <Badge variant="neutral" size="sm">zlecenie bez kanału — tylko sucha próba</Badge>;
  if (writes.killed) return <Badge variant="danger" size="sm">zapis wstrzymany awaryjnie</Badge>;
  const on = !!writes.writes_enabled;
  const can = !!writes.can_toggle;
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-ink-600 dark:text-ink-300">Zapis na konto klienta{count > 1 ? ` (kanały: ${count})` : ''}</span>
      <button
        type="button"
        disabled={!can || busy}
        onClick={() => onChange(!on)}
        title={can ? (on ? 'Wyłącz zapis — zostanie tylko sucha próba' : count > 1 ? 'Włącz zapis we wszystkich kanałach z tego wgrania' : 'Włącz zapis dla kanału tego zlecenia') : 'Zapis włącza właściciel organizacji albo administrator'}
        className={cn('relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50', on ? 'bg-amber-500' : 'bg-ink-300 dark:bg-ink-600')}
      >
        <span className={cn('inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform', on ? 'translate-x-5' : 'translate-x-0.5')} />
      </button>
      <span className={cn('text-xs font-semibold', on ? 'text-amber-600 dark:text-amber-400' : 'text-ink-500 dark:text-ink-400')}>{on ? 'WŁĄCZONY' : writes.partial ? `częściowo (${writes.partial.on} z ${writes.partial.total})` : 'wyłączony'}</span>
    </div>
  );
}

function PublishConfirm({ count, stage, clientName, writesEnabled, busy, onCancel, onConfirm }) {
  const what = stage === 'first' ? '1 produkt (pierwszy, testowy)' : `${count} ${count === 1 ? 'produkt' : count < 5 ? 'produkty' : 'produktów'}`;
  return (
    <div className="fixed inset-0 z-50 bg-ink-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white dark:bg-ink-900 rounded-xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="px-6 pt-5 pb-4">
          <div className="text-base font-semibold text-ink-900 dark:text-ink-50">Publikacja na Kaufland</div>
          <p className="mt-2 text-sm text-ink-600 dark:text-ink-300">
            Wyślemy dane produktu (tytuł, opis, zdjęcia, parametry, kategoria) dla <b>{what}</b> na konto Kauflanda{clientName ? <> klienta <b>{clientName}</b></> : ' klienta'}.
            Cenę i stan wystawia potem klient albo jego integrator.
          </p>
          <ul className="mt-3 text-xs text-ink-500 dark:text-ink-400 grid gap-1">
            <li>• Kaufland przetwarza dane od kilku minut do 1–3 dni.</li>
            <li>• Silnik zatrzyma się sam, gdy odrzuceń będzie ponad 5%.</li>
            <li>• Każdy zapis zostaje w dzienniku poniżej.</li>
          </ul>
          {!writesEnabled && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200 px-3 py-2 text-xs">
              Zapis na konto klienta jest teraz <b>wyłączony</b> (bezpiecznik). Przycisk poniżej najpierw go włączy, a potem opublikuje.
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-ink-100 dark:border-ink-800">
          <Button variant="ghost" onClick={onCancel}>Anuluj</Button>
          <Button loading={busy} onClick={() => onConfirm(!writesEnabled)}>{writesEnabled ? `Publikuj ${what}` : `Włącz zapis i publikuj`}</Button>
        </div>
      </div>
    </div>
  );
}

function PublishCard({ order, rows, isAdmin, onRefresh, onConnectAccount }) {
  const [busy, setBusy] = useState(null);
  // 2.3 (plan v12): połączenie Kaufland klienta — bez niego edge odrzuca publikację (no_kaufland_connection),
  // więc zamiast „Publikuj” karta pokazuje „podepnij konto”. null = jeszcze nie sprawdzone.
  const [kfConn, setKfConn] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!order?.tenant_id) { setKfConn(null); return undefined; }
    supabase.from('connections').select('id, status, tested_at, last_error').eq('tenant_id', order.tenant_id).eq('system', 'kaufland').limit(1).then(({ data }) => {
      if (alive) setKfConn(data?.[0] ? { id: data[0].id, status: data[0].status, tested_at: data[0].tested_at, last_error: data[0].last_error } : false);
    });
    return () => { alive = false; };
  }, [order?.tenant_id]);
  // Poprawka v13 (24.10, B): stan połączenia w karcie + „Testuj połączenie” (edge connections-test, sekret z Vault).
  const [testingConn, setTestingConn] = useState(false);
  const testConnection = async () => {
    if (!kfConn?.id) return;
    setTestingConn(true);
    try {
      const d = await callEdge(CONNECTIONS_TEST_URL, { connection_id: kfConn.id });
      const ok = d?.ok !== false && !d?.error;
      setKfConn(c => ({ ...c, status: ok ? 'ok' : 'error', tested_at: new Date().toISOString(), last_error: ok ? null : (d?.error || 'test nieudany') }));
      if (ok) toast.success('Połączenie Kaufland działa'); else toast.error(`Połączenie Kaufland: ${d?.error || 'test nieudany'}`);
    } catch (e) {
      setKfConn(c => ({ ...c, status: 'error', tested_at: new Date().toISOString(), last_error: e.message }));
      toast.error(`Połączenie Kaufland: ${e.message}`);
    } finally { setTestingConn(false); }
  };
  const noConnection = !!order?.tenant_id && kfConn === false;
  const connectionBroken = !!kfConn && kfConn.status && kfConn.status !== 'ok';
  const [events, setEvents] = useState(null);
  const [open, setOpen] = useState(null);
  const [lastRun, setLastRun] = useState(null);
  const [writes, setWrites] = useState(null); // { writes_enabled, is_admin } z edge publish-order (action: info)
  const [confirm, setConfirm] = useState(null); // { stage, count }
  const [toggling, setToggling] = useState(false);

  const [writesErr, setWritesErr] = useState(null);
  const loadWrites = useCallback(async () => {
    setWritesErr(null);
    try { setWrites((await loadWritesInfo([order.id]))[0]); } catch (e) { setWrites(null); setWritesErr(e.message || 'błąd'); }
  }, [order.id]);
  useEffect(() => { loadWrites(); }, [loadWrites]);
  const setWritesEnabled = async (enabled) => {
    setToggling(true);
    setWrites(w => (w ? { ...w, writes_enabled: enabled } : w)); // optymistycznie
    try {
      const d = await callEdge(PUBLISH_ORDER_URL, { order_id: order.id, action: 'set_writes', enabled });
      setWrites(d);
      toast.success(enabled ? 'Zapis na konto klienta włączony' : 'Zapis wyłączony — działają tylko suche próby');
      return d.writes_enabled;
    } catch (e) { toast.error(e.message); loadWrites(); return false; } finally { setToggling(false); }
  };

  // v9/A4: publikować na żywo może admin albo właściciel organizacji klienta (edge zwraca can_toggle).
  const canWrite = isAdmin || !!writes?.can_toggle;
  const kf = rows.filter(r => r.platform === 'kaufland');
  const toSend = kf.filter(r => r.status === 'ready' && r.card_exists === false);
  const cardOk = kf.filter(r => r.status === 'ready' && r.card_exists === true);
  const sent = kf.filter(r => r.reasons.some(x => x.code === 'product_data_sent'));
  const live = kf.filter(r => r.status === 'live');
  const rejected = kf.filter(r => r.status === 'rejected');
  // B: sucha próba także dla wierszy „do przeglądu” / „czeka na klienta” — pokazuje blokady per produkt (nic nie idzie).
  const previewRows = kf.filter(r => (r.status === 'ready' && r.card_exists === false) || r.status === 'review' || r.status === 'waiting_client');

  const loadEvents = useCallback(async () => {
    const ids = rows.map(r => r.id);
    if (!ids.length) { setEvents([]); return; }
    const { data } = await supabase.from('listing_events').select('id, listing_id, created_at, event_type, request, response, actor').in('listing_id', ids.slice(0, 1000))
      .not('event_type', 'like', 'base_export%').not('event_type', 'like', 'sellasist_export%').not('event_type', 'like', 'sellasist_manufacturer%')
      .order('created_at', { ascending: false }).limit(50);
    setEvents(data || []);
  }, [rows]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  const STAGE_COUNT = { first: 1, batch: 20, rest: Infinity, all: Infinity };
  const askPublish = (stage) => setConfirm({ stage, count: Math.min(STAGE_COUNT[stage] ?? 1, toSend.length) });
  const run = async (action, stage, dryRun) => {
    const label = action === 'status' ? 'odświeżenie statusów' : dryRun ? 'sucha próba' : `publikacja (${Math.min(STAGE_COUNT[stage] ?? 1, toSend.length)} szt.)`;
    setBusy(`${action}:${stage}:${dryRun}`);
    try {
      const d = await callEdge(PUBLISH_ORDER_URL, { order_id: order.id, action, stage, dry_run: dryRun, ...(action === 'publish' && dryRun ? { include_review: true } : {}) });
      setLastRun({ ...d, started_at: Date.now(), events_before: (events || []).length });
      toast.success(`Uruchomiono: ${label}. Dziennik poniżej odświeży się sam przez 2 minuty.`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  // Po uruchomieniu: auto-odświeżanie dziennika co 10 s przez 2 minuty (Trigger pisze do listing_events asynchronicznie).
  useEffect(() => {
    if (!lastRun?.started_at) return undefined;
    const t = setInterval(() => {
      if (Date.now() - lastRun.started_at > 120_000) { clearInterval(t); return; }
      loadEvents(); onRefresh?.();
    }, 10_000);
    return () => clearInterval(t);
  }, [lastRun, loadEvents, onRefresh]);
  const waitedTooLong = lastRun?.started_at && Date.now() - lastRun.started_at > 90_000 && (events || []).length <= (lastRun.events_before || 0);

  const skuOf = (listingId) => rows.find(r => r.id === listingId)?.sku || listingId?.slice(0, 8);
  const noTenant = !order?.tenant_id;
  const canPublish = !noTenant && !noConnection;

  if (!kf.length) return null;
  return (
    <Card className="mb-5">
      <CardHeader className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <CardTitle>Publikacja na Kaufland</CardTitle>
          <CardDescription>
            {formatNumber(toSend.length)} do wysłania (nowa karta) · {formatNumber(cardOk.length)} z gotową kartą (ofertę wystawia klient/integrator) · {formatNumber(sent.length)} wysłanych · {formatNumber(live.length)} żywych · {formatNumber(rejected.length)} odrzuconych
          </CardDescription>
        </div>
        <WritesSwitch writes={writes} error={writesErr} onRetry={loadWrites} busy={toggling} onChange={setWritesEnabled} />
      </CardHeader>
      <CardBody className="grid gap-4">
        {noTenant && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200 px-4 py-3 text-sm">
            To zlecenie jest <b>bez klienta</b> — publikacja wymaga klienta z połączeniem Kaufland (klucze są w Vault połączenia). Uruchom zlecenie ponownie: <b>Nowe zlecenie → wybierz klienta</b>, a potem wróć tutaj. Raport gotowości i xlsx działają normalnie.
          </div>
        )}
        {noConnection && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200 px-4 py-3 text-sm flex flex-wrap items-center gap-3">
            <span className="flex-1 min-w-[16rem]">Klient <b>{order.tenant_name || ''}</b> nie ma jeszcze połączenia z Kauflandem — publikacja i sucha próba potrzebują kluczy API jego konta sprzedawcy. Podepnij konto: <b>Klienci → ten klient → Połączenie bezpośrednie z marketplace → Kaufland</b>.</span>
            {onConnectAccount && <Button size="sm" onClick={onConnectAccount}>Podepnij konto Kaufland</Button>}
          </div>
        )}
        {connectionBroken && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200 px-4 py-3 text-sm">
            Połączenie Kaufland klienta ma status <b>{kfConn.status}</b> — przetestuj je w zakładce Klienci, zanim opublikujesz.
          </div>
        )}
        {kfConn && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-ink-600 dark:text-ink-300">
            <span>Połączenie Kaufland:</span>
            <Badge variant={kfConn.status === 'ok' ? 'success' : kfConn.status === 'error' ? 'danger' : 'neutral'} size="sm">{kfConn.status === 'ok' ? 'działa' : kfConn.status === 'error' ? 'błąd' : (kfConn.status || 'nieprzetestowane')}</Badge>
            <span className="text-ink-500 dark:text-ink-400">ostatni test: {kfConn.tested_at ? formatDateTime(kfConn.tested_at) : 'nigdy'}</span>
            {kfConn.status === 'error' && kfConn.last_error && <span className="text-danger truncate max-w-[24rem]" title={kfConn.last_error}>{kfConn.last_error}</span>}
            <Button size="xs" variant="ghost" loading={testingConn} disabled={testingConn} onClick={testConnection} leftIcon={<RefreshCw className="w-3 h-3" />}>Testuj połączenie</Button>
          </div>
        )}
        {waitedTooLong && (
          <div className="rounded-lg border border-red-200 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200 px-4 py-3 text-sm">
            Run <code>{lastRun.run_id}</code> został przyjęty, ale po 90 s nie ma nowych wpisów w dzienniku. Najpewniej padł po stronie Trigger.dev (brak połączenia Kaufland u klienta albo task <code>publish-listings</code> nie jest wdrożony w tym środowisku). Sprawdź run w panelu Trigger.dev (Runs → wyszukaj id).
          </div>
        )}
        <div className="flex flex-wrap gap-2 items-center">
          <Button size="sm" variant="secondary" loading={busy === 'publish:all:true'} disabled={!canPublish || !previewRows.length || !!busy} onClick={() => run('publish', 'all', true)} title={toSend.length < previewRows.length ? `Także ${formatNumber(previewRows.length - toSend.length)} do przeglądu / czeka na klienta — pokaże blokady przy produktach, nic nie wyśle` : undefined}>Sucha próba — pokaż, co poleci{previewRows.length !== toSend.length ? ` (${formatNumber(previewRows.length)})` : ''}</Button>
          {canWrite && !noConnection && (
            <>
              <span className="w-px h-6 bg-ink-200 dark:bg-ink-700 mx-1" />
              <Button size="sm" loading={busy === 'publish:first:false'} disabled={!canPublish || !toSend.length || !!busy} onClick={() => askPublish('first')}>Publikuj: 1 produkt</Button>
              <Button size="sm" loading={busy === 'publish:batch:false'} disabled={!canPublish || !toSend.length || !!busy} onClick={() => askPublish('batch')}>Publikuj: 20</Button>
              <Button size="sm" loading={busy === 'publish:rest:false'} disabled={!canPublish || !toSend.length || !!busy} onClick={() => askPublish('rest')}>Publikuj: resztę</Button>
            </>
          )}
          <span className="w-px h-6 bg-ink-200 dark:bg-ink-700 mx-1" />
          <Button size="sm" variant="ghost" loading={busy === 'status:first:true'} disabled={!canPublish || !!busy || !(sent.length || live.length)} onClick={() => run('status', 'first', true)} leftIcon={<RefreshCw className="w-3.5 h-3.5" />}>Odśwież statusy z Kauflanda</Button>
        </div>
        <p className="text-2xs text-ink-500 dark:text-ink-400">Zapis idzie etapami: 1 produkt → sprawdzenie statusu → 20 → reszta; silnik zatrzymuje się, gdy odrzuceń jest ponad 5%. Kaufland przetwarza dane 1–3 dni — „żywe" pojawia się po „Odśwież statusy".</p>

        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-ink-700 dark:text-ink-300">Dziennik publikacji</div>
            <Button size="xs" variant="ghost" onClick={() => { loadEvents(); onRefresh?.(); }} leftIcon={<RefreshCw className="w-3 h-3" />}>Odśwież</Button>
          </div>
          {events == null ? <SkeletonRow /> : events.length === 0 ? (
            <p className="text-sm text-ink-500 dark:text-ink-400">Jeszcze nic — uruchom suchą próbę, a tu zobaczysz dokładny ładunek dla każdego produktu.</p>
          ) : (
            <div className="rounded-lg border border-ink-200 dark:border-ink-700 divide-y divide-ink-100 dark:divide-ink-800 max-h-80 overflow-y-auto">
              {events.map(ev => {
                const m = EVENT_LABEL[ev.event_type] || { label: ev.event_type, tone: 'neutral' };
                const isOpen = open === ev.id;
                const detail = ev.response?.error || ev.response?.message || ev.response?.status || '';
                return (
                  <div key={ev.id} className="text-sm">
                    <button type="button" onClick={() => setOpen(isOpen ? null : ev.id)} className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-ink-50 dark:hover:bg-ink-800/50">
                      <span className="text-2xs text-ink-400 whitespace-nowrap w-28">{formatDateTime(ev.created_at)}</span>
                      <Badge variant={m.tone} size="sm">{m.label}</Badge>
                      <span className="font-medium text-ink-900 dark:text-ink-50">{skuOf(ev.listing_id)}</span>
                      <span className="text-xs text-ink-500 dark:text-ink-400 truncate flex-1">{typeof detail === 'string' ? detail : JSON.stringify(detail)}</span>
                    </button>
                    {isOpen && (
                      <pre className="px-3 pb-3 text-2xs text-ink-700 dark:text-ink-300 whitespace-pre-wrap break-all max-h-64 overflow-y-auto bg-ink-50 dark:bg-ink-800/40">{JSON.stringify({ request: ev.request, response: ev.response }, null, 2)}</pre>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardBody>
      {confirm && (
        <PublishConfirm
          count={confirm.count}
          stage={confirm.stage}
          clientName={order.tenant_name}
          writesEnabled={!!writes?.writes_enabled}
          busy={!!busy || toggling}
          onCancel={() => setConfirm(null)}
          onConfirm={async (enableFirst) => {
            if (enableFirst && !(await setWritesEnabled(true))) return;
            const st = confirm.stage; setConfirm(null);
            await run('publish', st, false);
          }}
        />
      )}
    </Card>
  );
}

// T30: podgląd raportu w aplikacji. „Zapisz jako PDF" = okno drukowania przeglądarki z miejscem docelowym „Zapisz jako PDF"
// (Chrome/Edge mają je wbudowane) — plik ląduje w Pobranych pod podaną nazwą.
function PdfPreviewModal({ doc, onClose }) {
  const [frame, setFrame] = useState(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const print = () => {
    if (!frame?.contentWindow) return;
    try { frame.contentDocument.title = doc.name.replace(/\.pdf$/, ''); } catch (_) { /* cross-origin nie dotyczy srcDoc */ }
    frame.contentWindow.focus();
    frame.contentWindow.print();
  };
  const openTab = () => {
    const blob = new Blob([doc.html], { type: 'text/html;charset=utf-8' });
    window.open(URL.createObjectURL(blob), '_blank');
  };
  const downloadHtml = () => {
    const blob = new Blob([doc.html], { type: 'text/html;charset=utf-8' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = doc.name.replace(/\.pdf$/, '.html'); a.click();
  };
  return (
    <div className="fixed inset-0 z-50 bg-ink-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-ink-900 rounded-xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-ink-200 dark:border-ink-700">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-ink-900 dark:text-ink-50">Raport gotowości — podgląd</div>
            <div className="text-2xs text-ink-500 dark:text-ink-400 truncate">{doc.name} · w oknie drukowania wybierz miejsce docelowe „Zapisz jako PDF"</div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={print} leftIcon={<Download className="w-3.5 h-3.5" />}>Zapisz jako PDF / Drukuj</Button>
            <Button size="sm" variant="ghost" onClick={openTab}>Otwórz w nowej karcie</Button>
            <Button size="sm" variant="ghost" onClick={downloadHtml}>Pobierz HTML</Button>
            <Button size="sm" variant="ghost" onClick={onClose}>Zamknij</Button>
          </div>
        </div>
        <iframe ref={setFrame} title="Raport gotowości" srcDoc={doc.html} className="flex-1 w-full bg-white" />
      </div>
    </div>
  );
}

export default function ReadinessPage({ isAdmin = false, initialOrderId = null, onInitialOrderUsed = null, canOrderAplus = false, onCreditsChange = null, onConnectAccount = null }) {
  const [orders, setOrders] = useState(null);
  const [ordersFresh, setOrdersFresh] = useState(false); // lista z sieci (nie tylko z cache) — dopiero wtedy wybór „najnowszego”
  const [orderId, setOrderId] = useState(initialOrderId || '');
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [loadingRows, setLoadingRows] = useState(false);
  const [catNames, setCatNames] = useState({});
  const [picker, setPicker] = useState(null);
  const [regenRow, setRegenRow] = useState(null);
  const [pdfHtml, setPdfHtml] = useState(null); // T30: podgląd raportu w aplikacji (modal), zapis przez okno drukowania

  // Zlecenia z klientem (K2) — te mają kanał, więc dla nich worker pisze listings.
  // PERF-UX (21.10): lista zleceń i wiersze gotowości od razu z cache (ostatni stan), świeże w tle.
  const loadOrders = useCallback(async () => {
    try {
      await loadWithCache('readiness:orders', async () => {
        const [{ data: o }, { data: t }] = await Promise.all([
          supabase.from('orders').select('id, created_at, marketplace, language, product_count, status, filename, tenant_id, channels, config').order('created_at', { ascending: false }).limit(100),
          supabase.from('tenants').select('id, name'),
        ]);
        const tn = Object.fromEntries((t || []).map(x => [x.id, x.name]));
        return (o || []).map(x => ({ ...x, tenant_name: x.tenant_id ? tn[x.tenant_id] || '—' : null }));
      }, (list, fromCache) => { setOrders(list); if (!fromCache) setOrdersFresh(true); });
    } catch (e) { toast.error(e.message); setOrdersFresh(true); }
  }, []);
  useEffect(() => { loadOrders(); }, [loadOrders]);
  // 21.10: wybrane zlecenie w adresie (?page=readiness&order=<id>) — F5 zostaje na tym zleceniu.
  useEffect(() => { setUrlParams({ order: orderId || null }); }, [orderId]);
  // T2.1: wejście z kolejki zleceń = to zlecenie; zwykłe wejście = najnowsze zlecenie klienta (wcześniej pusty ekran „Wybierz zlecenie”).
  // Wejście z kolejki / z toastu „Zlecenie gotowe” (także gdy Gotowość jest już otwarta) = to zlecenie.
  useEffect(() => {
    if (!initialOrderId) return;
    setOrderId(initialOrderId); setFilter('all'); setQ('');
    if (onInitialOrderUsed) onInitialOrderUsed();
  }, [initialOrderId]);
  useEffect(() => {
    if (orderId || !orders?.length || !ordersFresh) return;
    const latest = orders.find(o => o.tenant_id && o.status === 'done') || orders.find(o => o.tenant_id);
    if (latest) setOrderId(latest.id);
  }, [orders, ordersFresh]);

  // Kolumny jawnie (bez `*`): `listings` ma 5000 wierszy z jsonb — mniej bajtów, mniej parsowania (PERF-UX A6).
  const LISTING_COLS = 'id, order_id, row_index, channel_id, tenant_id, platform, market, marketplace, storefront, status, reasons, category_id, category_origin, card_exists, price, price_cents, currency, source_price, source_currency, platform_offer_id, sku, ean, is_live, situation, situation_reason, updated_at';
  const loadRows = useCallback(async () => {
    if (!orderId) { setRows(null); return; }
    if (!getCached(`readiness:rows:${orderId}`)) setRows(null); // nowe zlecenie bez cache → szkielet, nie cudze wiersze
    try {
      await loadWithCache(`readiness:rows:${orderId}`, async () => {
        let { data, error } = await supabase.from('listings').select(LISTING_COLS).eq('order_id', orderId).limit(5000);
        if (error && /order_id/.test(error.message || '')) {
          // Migracja K3 jeszcze nie weszła — pokaż wiersze po kliencie i rynku (stary kształt).
          const o = orders?.find(x => x.id === orderId);
          const res = await supabase.from('listings').select(LISTING_COLS).eq('tenant_id', o?.tenant_id ?? '00000000-0000-0000-0000-000000000000').limit(5000);
          data = res.data; error = res.error;
        }
        if (error) throw error;
        return data || [];
      }, (data) => { setRows((data || []).map(normalize).sort(byFileOrder)); setLoadingRows(false); });
    } catch (e) { toast.error(e.message); setRows(r => r || []); setLoadingRows(false); }
  }, [orderId, orders]);
  useEffect(() => { loadRows(); }, [loadRows]);
  // Zlecenie uruchomione / zakończone gdziekolwiek w aplikacji → lista zleceń od razu świeża (i wiersze, gdy to wybrane).
  const orderIdRef = useRef(orderId);
  orderIdRef.current = orderId;
  const loadRowsRef = useRef(loadRows);
  loadRowsRef.current = loadRows;
  useEffect(() => onOrdersChanged((d) => {
    loadOrders();
    if ((d.finished || []).includes(orderIdRef.current)) loadRowsRef.current();
  }), [loadOrders]);

  const order = orders?.find(o => o.id === orderId) || null;

  // Gotowość 21.10: integrator kanału zlecenia → jedna karta zapisu (BASE / Sellasist) albo karta Kauflanda (direct).
  const setup = useOrderSetup(order);
  const integrator = setup?.integrator || null;
  const integratorCfg = integrator ? INTEGRATORS[integrator] : null;
  const channelsById = useMemo(() => Object.fromEntries((setup?.channels || []).map(c => [c.id, c])), [setup]);
  const siblings = useMemo(() => {
    if (!integrator || !setup) return [];
    return siblingOrders(order, orders).filter(o => channelIntegrator(channelsById[Array.isArray(o.channels) ? o.channels[0] : ''], setup.conns) === integrator);
  }, [order, orders, integrator, setup, channelsById]);
  const showKaufland = !!setup && !integrator && (rows || []).some(r => r.platform === 'kaufland')
    && (setup.conns.some(c => c.system === 'kaufland') || !setup.conns.some(c => INTEGRATOR_SYSTEMS.includes(c.system)));
  // INT-ALLEGRO (22.10): kanał Allegro bez integratora → karta „Publikacja na Allegro” i kolumna „Allegro”.
  const allegroDirect = !!setup && !integrator && isAllegroDirect(setup.channel, setup.conns);
  const allegroConn = allegroDirect ? (setup.conns.find(c => c.id === setup.channel?.connection_id && c.system === 'allegro') || setup.conns.find(c => c.system === 'allegro') || null) : null;
  // INT-AMAZON (23.10): kanał Amazon bez integratora → karta „Publikacja na Amazon” i kolumna „Amazon”.
  const amazonDirect = !!setup && !integrator && setup.channel?.platform === 'amazon';
  const amazonConn = amazonDirect ? (setup.conns.find(c => c.id === setup.channel?.connection_id && c.system === 'amazon') || setup.conns.find(c => c.system === 'amazon') || null) : null;
  const exportCfg = integratorCfg || (allegroDirect ? ALLEGRO_CFG : amazonDirect ? AMAZON_CFG : showKaufland ? KAUFLAND_CFG : null);
  const [exportStatus, , loadExportSince] = useExportStatus(exportCfg ? rows : null, exportCfg?.prefix, orderId, exportCfg?.outcomes, exportCfg?.putKind);
  const [exportOpen, setExportOpen] = useState(null);

  // Po „Sucha próba …” / „Zapisz do …”: worker pisze zdarzenia asynchronicznie (na dev 30–90 s). Odpytujemy co 5 s
  // (maks. 3 min), aż każdy gotowy produkt zlecenia ma zdarzenie nowsze niż przed kliknięciem. Tabela się nie przeładowuje —
  // nowe zdarzenia dopisują się do kolumny BASE / Sellasist.
  const [exportRun, setExportRun] = useState(null); // { since, startedAt, dry, total, short }
  const exportReady = useMemo(() => (integrator ? readyForIntegrator(integrator, rows || []) : allegroDirect ? readyForIntegrator('allegro', rows || []) : amazonDirect ? readyForIntegrator('amazon', rows || []) : []), [integrator, allegroDirect, amazonDirect, rows]);
  const exportProgress = useMemo(() => {
    if (!exportRun) return null;
    // Audyt 24.10: licznik po wysłanych row_index (Amazon/Allegro: 1 / 3 / wszystkie), nie po wszystkich gotowych.
    const done = countExportDone({ rows, ready: exportReady, statusByListing: exportStatus, run: exportRun });
    return { done, total: exportRun.total, dry: exportRun.dry };
  }, [exportRun, exportReady, exportStatus, rows]);
  useEffect(() => { setExportRun(null); }, [orderId]);
  const startExportPolling = useCallback((dry, total, rowIndexes) => {
    const since = exportStatus ? latestEventAt(exportStatus) : new Date(Date.now() - 60_000).toISOString();
    const idx = Array.isArray(rowIndexes) && rowIndexes.length ? new Set(rowIndexes) : null;
    setExportRun({ since, startedAt: Date.now(), dry, total: idx ? idx.size : (total ?? exportReady.length), rowIndexes: idx, short: exportCfg?.short || '' });
  }, [exportStatus, exportReady, exportCfg]);
  const loadSinceRef = useRef(loadExportSince);
  loadSinceRef.current = loadExportSince;
  useEffect(() => {
    if (!exportRun) return undefined;
    const t = setInterval(() => {
      if (Date.now() - exportRun.startedAt > 180_000) {
        setExportRun(null);
        toast.info('Nie każdy produkt ma już wynik — kliknij „Odśwież” za chwilę.');
        return;
      }
      loadSinceRef.current(exportRun.since);
    }, 5000);
    return () => clearInterval(t);
  }, [exportRun]);
  useEffect(() => {
    if (!exportRun || !exportProgress) return;
    if (exportProgress.done >= exportProgress.total) {
      setExportRun(null);
      toast.success(`${exportRun.dry ? 'Sucha próba zakończona' : exportRun.short === 'Allegro' ? 'Wystawianie szkiców zakończone' : `Zapis do ${exportRun.short} zakończony`} — wynik w tabeli.`);
      if (exportRun.short === 'Allegro' && !exportRun.dry) loadRows();
    }
  }, [exportRun, exportProgress, loadRows]);

  // Nazwy kategorii Kauflanda dla id w tabeli (po 30 na wywołanie; wynik trzymany w stanie strony).
  useEffect(() => {
    if (!rows?.length) return;
    const ids = [...new Set(rows.filter(r => r.platform === 'kaufland' && r.category_id).map(r => String(r.category_id)))].filter(id => !catNames[id]);
    if (!ids.length) return;
    (async () => {
      const next = {};
      for (let i = 0; i < ids.length; i += 30) {
        try {
          const d = await callEdge(KAUFLAND_CATEGORIES_URL, { tenant_id: order?.tenant_id || undefined, storefront: rows[0].market || 'de', ids: ids.slice(i, i + 30).map(Number) });
          (d.categories || []).forEach(c => { next[String(c.id)] = c; });
        } catch (_) { break; }
      }
      if (Object.keys(next).length) setCatNames(prev => ({ ...prev, ...next }));
    })();
  }, [rows]);

  const counts = useMemo(() => {
    const c = Object.fromEntries(STATUS_ORDER.map(s => [s, 0]));
    (rows || []).forEach(r => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (rows || []).filter(r =>
      (filter === 'all' || r.status === filter) &&
      (!needle || r.sku.toLowerCase().includes(needle) || r.ean.toLowerCase().includes(needle) || r.reasons.some(x => reasonText(x).toLowerCase().includes(needle)))
    );
  }, [rows, filter, q]);

  const exportXlsx = () => {
    if (!rows?.length) return;
    const wb = XLSX.utils.book_new();
    const summary = STATUS_ORDER.map(s => ({ Status: STATUS[s].label, Liczba: counts[s] || 0 }));
    summary.push({ Status: 'Razem', Liczba: rows.length });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summary), 'Podsumowanie');
    const all = rows.map(r => ({
      'Lp.': r.row_index != null ? r.row_index + 1 : '', SKU: r.sku, EAN: r.ean, Platforma: PLATFORM_LABEL[r.platform] || r.platform, Rynek: r.market.toUpperCase(),
      Status: STATUS[r.status]?.label || r.status, Powody: r.reasons.map(reasonText).join('; '),
      'Karta istnieje': r.card_exists == null ? '' : r.card_exists ? 'tak' : 'nie',
      Kategoria: r.category_id || '', 'Nazwa kategorii': r.category_id ? (catNames[String(r.category_id)]?.name || '') : '', 'Źródło kategorii': r.category_origin || '',
      Cena: r.price ?? '', Waluta: r.price != null ? r.currency : '', 'Cena z pliku': r.source_price ?? '', 'Waluta z pliku': r.source_price != null ? r.source_currency : '', Aktualizacja: r.updated_at ? formatDateTime(r.updated_at) : '',
      ...(exportCfg ? { [exportCfg.short]: exportText(exportStatus?.[r.id], exportCfg) } : {}),
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(all), 'Produkty');
    const byStatus = (s, name) => {
      const part = all.filter((_, i) => rows[i].status === s);
      if (part.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(part), name);
    };
    byStatus('waiting_client', 'Czeka na klienta');
    byStatus('review', 'Do przeglądu');
    byStatus('rejected', 'Odrzucone');
    const name = `gotowosc_${(order?.tenant_name || 'klient').replace(/[^\w-]+/g, '_')}_${(order?.filename || orderId.slice(0, 8)).replace(/\.(xlsx|csv)$/i, '')}.xlsx`;
    XLSX.writeFile(wb, name);
  };

  const exportClientFile = async () => {
    const part = (rows || []).filter(r => r.status === 'waiting_client');
    if (!part.length) return toast.info('Nic nie czeka na klienta — wszystko gotowe albo w przeglądzie.');
    let names = {};
    try {
      const { data } = await supabase.from('batch_results').select('row_index, original_data').eq('order_id', orderId).limit(5000);
      (data || []).forEach(b => {
        const od = b.original_data || {};
        const k = Object.keys(od).find(x => /^(nazwa|name|tytuł|title)$/i.test(x.trim())) || Object.keys(od).find(x => /nazw|name/i.test(x));
        if (k) names[b.row_index] = String(od[k]).slice(0, 120);
      });
    } catch { names = {}; }
    const items = [];
    part.forEach(r => (r.reasons.length ? r.reasons : [{ code: 'other' }]).forEach(x => {
      if (!['weee', 'battg', 'ean', 'gpsr_contact', 'image', 'price', 'missing_attribute', 'ean_duplicate', 'platform_rejected', 'other'].includes(x.code)) return; // tylko braki po stronie klienta
      const f = clientFix(x);
      items.push({ SKU: r.sku, EAN: r.ean, Produkt: names[r.row_index] || '', 'Co uzupełnić': f.what, 'Jak / gdzie': f.how });
    }));
    const byWhat = {};
    items.forEach(i => { byWhat[i['Co uzupełnić']] = byWhat[i['Co uzupełnić']] || { 'Co uzupełnić': i['Co uzupełnić'], 'Ile produktów': 0, 'Jak / gdzie': i['Jak / gdzie'].replace(/ \(.*\)$/, '') }; byWhat[i['Co uzupełnić']]['Ile produktów'] += 1; });
    const wb = XLSX.utils.book_new();
    const intro = [
      { A: `Co od Ciebie potrzebujemy — ${order?.tenant_name || ''}` },
      { A: `${part.length} z ${rows.length} produktów czeka na Twoje dane. Gdy je uzupełnisz, wystawimy je przy najbliższym biegu.` },
      { A: '' },
    ];
    const ws1 = XLSX.utils.json_to_sheet(intro, { skipHeader: true });
    XLSX.utils.sheet_add_json(ws1, Object.values(byWhat).sort((a, b) => b['Ile produktów'] - a['Ile produktów']), { origin: 'A4' });
    ws1['!cols'] = [{ wch: 38 }, { wch: 14 }, { wch: 110 }];
    XLSX.utils.book_append_sheet(wb, ws1, 'Podsumowanie');
    const ws2 = XLSX.utils.json_to_sheet(items);
    ws2['!cols'] = [{ wch: 18 }, { wch: 15 }, { wch: 50 }, { wch: 34 }, { wch: 110 }];
    XLSX.utils.book_append_sheet(wb, ws2, 'Produkty');
    XLSX.writeFile(wb, `do_uzupelnienia_${(order?.tenant_name || 'klient').replace(/[^\w-]+/g, '_')}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // T30: raport PDF jednym klikiem — widok do druku w nowej karcie (Ctrl+P → „Zapisz jako PDF" otwiera się samo).
  const exportPdf = () => {
    if (!rows?.length) return;
    const esc = (s) => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    const title = `Raport gotowości — ${order?.tenant_name || 'bez klienta'} — ${order?.filename || orderId.slice(0, 8)}`;
    const tone = { live: '#16a34a', ready: '#2563eb', waiting_client: '#d97706', review: '#7c3aed', rejected: '#dc2626', error: '#dc2626' };
    const summaryRows = STATUS_ORDER.map(s => `<tr><td><span class="dot" style="background:${tone[s] || '#999'}"></span>${esc(STATUS[s].label)}</td><td class="n">${counts[s] || 0}</td><td class="n">${rows.length ? Math.round(((counts[s] || 0) / rows.length) * 100) : 0}%</td></tr>`).join('');
    const section = (s, intro) => {
      const part = rows.filter(r => r.status === s);
      if (!part.length) return '';
      return `<h2 style="color:${tone[s] || '#333'}">${esc(STATUS[s].label)} — ${part.length}</h2><p class="muted">${esc(intro)}</p>
<table><thead><tr><th>SKU</th><th>EAN</th><th>Kanał</th><th>Powody / co zrobić</th><th>Kategoria</th></tr></thead><tbody>${part.map(r => `<tr><td>${esc(r.sku)}</td><td>${esc(r.ean)}</td><td>${esc(PLATFORM_LABEL[r.platform] || r.platform)} ${esc(r.market.toUpperCase())}</td><td>${esc(r.reasons.map(reasonText).join('; ') || '—')}</td><td>${esc(r.category_id ? (catNames[String(r.category_id)]?.name || r.category_id) : '—')}</td></tr>`).join('')}</tbody></table>`;
    };
    const html = `<!doctype html><html lang="pl"><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>body{font-family:Inter,Segoe UI,Arial,sans-serif;color:#111;margin:32px;font-size:12px}h1{font-size:20px;margin:0 0 4px}h2{font-size:15px;margin:22px 0 4px}.muted{color:#666;margin:0 0 8px}table{border-collapse:collapse;width:100%;margin-bottom:10px}th,td{border:1px solid #ddd;padding:5px 7px;text-align:left;vertical-align:top}th{background:#f3f4f6;font-weight:600}td.n{text-align:right}.dot{display:inline-block;width:9px;height:9px;border-radius:50%;margin-right:6px}.sum{width:auto;min-width:320px}@media print{body{margin:14mm}h2{break-after:avoid}tr{break-inside:avoid}}</style></head><body>
<h1>${esc(title)}</h1><p class="muted">Wygenerowano ${esc(formatDateTime(new Date().toISOString()))} · ${rows.length} produktów · MAMS by Mediafy</p>
<h2>Podsumowanie</h2><table class="sum"><thead><tr><th>Status</th><th>Liczba</th><th>Udział</th></tr></thead><tbody>${summaryRows}<tr><th>Razem</th><th class="n">${rows.length}</th><th class="n">100%</th></tr></tbody></table>
${section('waiting_client', 'Te produkty czekają na uzupełnienie po stronie klienta (zdjęcia, dane producenta/GPSR, braki w pliku).')}
${section('review', 'Do decyzji osoby prowadzącej: kategoria, treść albo parametry wymagają przeglądu.')}
${section('rejected', 'Odrzucone przez platformę — powód w kolumnie obok.')}
${section('error', 'Błędy techniczne w czasie biegu — do ponowienia.')}
${section('ready', 'Gotowe do publikacji — nic nie blokuje.')}
${section('live', 'Już żywe na platformie.')}
</body></html>`;
    setPdfHtml({ html, name: `gotowosc_${(order?.tenant_name || 'klient').replace(/[^\w-]+/g, '_')}_${(order?.filename || orderId.slice(0, 8)).replace(/\.(xlsx|csv)$/i, '')}.pdf` });
  };

  // Zlecenia bez klienta też są na liście (oznaczone) — np. gdy klient został usunięty po biegu
  // (orders.tenant_id → NULL) albo zlecenie poszło starym trybem. Dla nich gotowość może być pusta.
  const orderLabel = (o) => `${o.tenant_name ? o.tenant_name : 'bez klienta'} · ${o.marketplace || '?'} ${o.language || ''} · ${formatNumber(o.product_count)} szt. · ${formatDateTime(o.created_at)}${o.filename ? ' · ' + o.filename : ''}`;
  const orderOptions = (orders || []).filter(o => o.status === 'done' || o.tenant_id).map(orderLabel);
  const ordersNoTenant = 0;

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Gotowość"
        subtitle="Co z katalogu klienta jest żywe, co gotowe do publikacji, a co czeka na klienta albo na Twój przegląd."
        action={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => { loadOrders(); loadRows(); }} leftIcon={<RefreshCw className="w-4 h-4" />}>Odśwież</Button>
            <Button variant="secondary" onClick={exportClientFile} disabled={!counts?.waiting_client} leftIcon={<Download className="w-4 h-4" />} title="Plik dla klienta: co ma uzupełnić, po ludzku">Braki dla klienta{counts?.waiting_client ? ` (${counts.waiting_client})` : ''}</Button>
            <Button variant="secondary" onClick={exportPdf} disabled={!rows?.length} leftIcon={<Download className="w-4 h-4" />}>Raport PDF</Button>
            <Button onClick={exportXlsx} disabled={!rows?.length} leftIcon={<Download className="w-4 h-4" />}>Pobierz xlsx</Button>
          </div>
        }
      />

      <Card className="mb-5">
        <CardBody className="grid gap-3 sm:grid-cols-[1fr_auto] items-end">
          <div>
            <div className="text-sm font-medium text-ink-700 dark:text-ink-300 mb-1.5">Zlecenie</div>
            {orders == null ? <SkeletonRow /> : orderOptions.length === 0 ? (
              <p className="text-sm text-ink-500 dark:text-ink-400">Nie ma jeszcze zakończonych zleceń. Uruchom zlecenie w „Nowe zlecenie" z wybranym klientem — tu pojawi się jego gotowość.</p>
            ) : (
              <Select
                value={order ? orderLabel(order) : ''}
                onChange={(label) => { const o = orders.find(x => orderLabel(x) === label); setOrderId(o ? o.id : ''); setFilter('all'); setQ(''); }}
                options={orderOptions}
                placeholder="Wybierz zlecenie klienta…"
              />
            )}
          </div>
          {order && <Badge variant={order.status === 'done' ? 'success' : 'neutral'} size="lg">{order.status}</Badge>}
        </CardBody>
      </Card>

      {orderId && (
        rows == null || loadingRows ? (
          <div className="grid gap-3"><SkeletonRow /><SkeletonRow /><SkeletonRow /></div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<ClipboardCheck className="w-7 h-7" />}
            title="Brak wierszy gotowości dla tego zlecenia"
            description={!order?.tenant_id ? 'To zlecenie nie ma klienta (klient usunięty po biegu albo zlecenie poszło bez klienta) — nie ma kanału, więc nie ma gotowości. Uruchom je ponownie z klientem.' : order?.status === 'done' ? 'Zlecenie jest zakończone, ale silnik jeszcze nie zapisuje gotowości (tabela listings, kontrakt K3 — w toku po stronie silnika). Plik wynikowy w Historii ma już kolumny card_exists i param_status.' : 'Silnik zapisuje gotowość po biegu. Wróć, gdy zlecenie będzie zakończone.'}
          />
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
              {STATUS_ORDER.map(s => {
                const m = STATUS[s]; const Icon = m.icon; const on = filter === s;
                return (
                  <button key={s} type="button" onClick={() => setFilter(on ? 'all' : s)} title={m.hint}
                    className={cn('text-left rounded-xl border p-4 transition-colors bg-white dark:bg-ink-900', on ? 'border-brand-500 ring-4 ring-brand-500/10' : 'border-ink-200 dark:border-ink-800 hover:border-ink-300 dark:hover:border-ink-700')}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-2xs uppercase tracking-wide font-bold text-ink-500 dark:text-ink-400">{m.label}</span>
                      <Icon className={cn('w-4 h-4', TONE_ICON[m.tone])} />
                    </div>
                    <div className="mt-2 text-2xl font-bold tracking-tighter text-ink-900 dark:text-ink-50 tabular-nums">{formatNumber(counts[s] || 0)}</div>
                    <div className="text-2xs text-ink-500 dark:text-ink-400 mt-0.5">{rows.length ? Math.round(100 * (counts[s] || 0) / rows.length) : 0}%</div>
                  </button>
                );
              })}
            </div>

            {/* QA-PANEL: oceny opisów NIE w aplikacji klienta (decyzja Maćka 21.10) — są w centrum dowodzenia (/oceny, tabela quality_ratings). */}
            {/* Jedna karta zapisu: integrator kanału (BASE / Sellasist) albo Kaufland bezpośrednio — nigdy obie. */}
            {integratorCfg && <IntegratorCard key={`${orderId}:${integrator}`} order={order} rows={rows} isAdmin={isAdmin} integrator={integrator} siblings={siblings} channelsById={channelsById} onStarted={startExportPolling} progress={exportProgress} onRefresh={loadRows} />}
            {showKaufland && <PublishCard order={order} rows={rows} isAdmin={isAdmin} onRefresh={loadRows} onConnectAccount={onConnectAccount} />}
            {allegroDirect && <AllegroCard key={`${orderId}:allegro`} order={order} rows={rows} isAdmin={isAdmin} conn={allegroConn} onStarted={startExportPolling} progress={exportProgress} onRefresh={loadRows} onConnectAccount={onConnectAccount} />}
            {amazonDirect && <AmazonCard key={`${orderId}:amazon`} order={order} rows={rows} conn={amazonConn} channel={setup.channel} onStarted={startExportPolling} progress={exportProgress} onRefresh={loadRows} onConnectAccount={onConnectAccount} />}

            <Card>
              <CardHeader className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <CardTitle>Produkty{filter !== 'all' ? ` · ${STATUS[filter].label.toLowerCase()}` : ''}</CardTitle>
                  <CardDescription>{formatNumber(visible.length)} z {formatNumber(rows.length)}{filter !== 'all' ? ' — kliknij kafelek ponownie, żeby zdjąć filtr' : ''}</CardDescription>
                </div>
                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                  {exportProgress && exportCfg && <ExportProgress progress={exportProgress} short={exportCfg.short} />}
                  <Button size="sm" variant="ghost" onClick={loadRows} leftIcon={<RefreshCw className="w-3.5 h-3.5" />} title="Odśwież produkty i wyniki zapisu bez przeładowania strony">Odśwież</Button>
                  <div className="w-full sm:w-72"><Input value={q} onChange={e => setQ(e.target.value)} placeholder="SKU, EAN albo powód…" leftIcon={<Search className="w-4 h-4" />} /></div>
                </div>
              </CardHeader>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-ink-50 dark:bg-ink-800/50 text-2xs uppercase tracking-wide text-ink-500 dark:text-ink-400">
                    <tr>
                      <th className="text-right pl-5 pr-2 py-3 font-bold w-12" title="Pozycja w pliku klienta">Lp.</th>
                      <th className="text-left px-5 py-3 font-bold">SKU</th>
                      <th className="text-left px-5 py-3 font-bold">EAN</th>
                      <th className="text-left px-5 py-3 font-bold">Kanał</th>
                      <th className="text-left px-5 py-3 font-bold">Status</th>
                      {exportCfg && <th className="text-left px-5 py-3 font-bold">{exportCfg.short}</th>}
                      <th className="text-left px-5 py-3 font-bold">Powody</th>
                      <th className="text-left px-5 py-3 font-bold">Kategoria</th>
                      <th className="text-right px-5 py-3 font-bold">Cena</th>
                      <th className="px-3 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-100 dark:divide-ink-800">
                    {visible.slice(0, 500).map(r => {
                      const m = STATUS[r.status] || STATUS.error;
                      return (
                        <tr key={r.id} className="hover:bg-ink-50/70 dark:hover:bg-ink-800/40">
                          <td className="pl-5 pr-2 py-3 text-right text-xs text-ink-400 dark:text-ink-500 tabular-nums">{r.row_index != null ? r.row_index + 1 : '—'}</td>
                          <td className="px-5 py-3 font-medium text-ink-900 dark:text-ink-50 whitespace-nowrap">{r.sku || '—'}</td>
                          <td className="px-5 py-3 text-ink-600 dark:text-ink-300 tabular-nums whitespace-nowrap">{r.ean || '—'}</td>
                          <td className="px-5 py-3 text-ink-700 dark:text-ink-300 whitespace-nowrap">{PLATFORM_LABEL[r.platform] || r.platform || '—'} {r.market ? r.market.toUpperCase() : ''}{r.card_exists === true ? <span className="ml-2 text-2xs text-ink-400">karta jest</span> : r.card_exists === false ? <span className="ml-2 text-2xs text-ink-400">nowa karta</span> : null}</td>
                          <td className="px-5 py-3"><Badge variant={m.tone} size="sm">{m.label}</Badge></td>
                          {exportCfg && (
                            <td className="px-5 py-3 whitespace-nowrap">
                              {exportStatus == null ? <span className="text-ink-300 dark:text-ink-600">…</span> : <ExportCell info={exportStatus[r.id]} cfg={exportCfg} rowStatus={r.status} onOpen={() => setExportOpen(r)} />}
                            </td>
                          )}
                          <td className="px-5 py-3 text-ink-700 dark:text-ink-300 max-w-md">
                            {r.reasons.length === 0 ? <span className="text-ink-400">—</span> : (
                              <ul className="space-y-0.5">{r.reasons.map((x, i) => <li key={i} className="text-xs leading-snug">{reasonText(x)}</li>)}</ul>
                            )}
                          </td>
                          <td className="px-5 py-3 text-xs text-ink-600 dark:text-ink-300">
                            <div className="flex items-center gap-2">
                              <span className="whitespace-nowrap">{r.category_id ? (catNames[String(r.category_id)]?.name || r.category_id) : '—'}</span>
                              {r.category_origin === 'suggested' && <Badge variant="info" size="sm">podpowiedź</Badge>}
                              {(r.category_origin === 'learned' || r.category_origin === 'client') && <span className="text-2xs text-ink-400">z mapy</span>}
                              {r.platform === 'kaufland' && <button type="button" onClick={() => setPicker(r)} className="text-2xs font-semibold text-brand-700 dark:text-brand-300 hover:underline whitespace-nowrap">{r.category_id ? 'zmień' : 'ustaw'}</button>}
                            </div>
                            {r.category_id && catNames[String(r.category_id)]?.path && <div className="text-2xs text-ink-400 dark:text-ink-500 truncate max-w-[220px]">{catNames[String(r.category_id)].path}</div>}
                          </td>
                          <td className="px-5 py-3 text-right tabular-nums text-ink-800 dark:text-ink-200 whitespace-nowrap">
                            {r.price != null ? `${r.price.toFixed(2)} ${r.currency}` : r.source_price != null ? <span title="Cena z pliku klienta — cena oferty policzy się po dodaniu reguły ceny (Klienci → klient → Reguła ceny)">{r.source_price.toFixed(2)} {r.source_currency}<span className="block text-2xs text-ink-400 dark:text-ink-500 font-normal">z pliku</span></span> : '—'}
                          </td>
                          <td className="px-3 py-3 text-right">
                            {order?.status === 'done' && r.row_index != null && (
                              <button type="button" onClick={() => setRegenRow(r)} title="Wygeneruj ponownie (regeneracja: płatna, darmowa przy błędzie programu albo z puli 1 % planu)" className="p-1.5 rounded-md text-ink-400 hover:text-brand-700 hover:bg-brand-50 dark:hover:bg-brand-900/20 dark:hover:text-brand-300">
                                <RotateCcw className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {visible.length > 500 && <p className="px-5 py-3 text-xs text-ink-500 dark:text-ink-400">Pokazuję 500 z {formatNumber(visible.length)} — zawęź filtrem albo pobierz xlsx.</p>}
              </div>
            </Card>
          </>
        )
      )}

      {/* Karty drugorzędne na dole. T2.8: A+ jako płatny dodatek — agencja i admin (konto klienta nie płaci kredytami). */}
      {orderId && order?.status === 'done' && canOrderAplus && <div className="mt-5"><AplusPanel key={orderId} orderId={orderId} onCreditsChange={onCreditsChange} /></div>}

      {!orderId && orders && orderOptions.length > 0 && (
        <EmptyState icon={<ClipboardCheck className="w-7 h-7" />} title="Wybierz zlecenie" description="Gotowość liczy się per zlecenie × kanał. Wybierz zlecenie klienta powyżej." />
      )}
      {picker && <CategoryPicker row={picker} order={order} onClose={() => setPicker(null)} onSaved={loadRows} />}
      {exportOpen && exportCfg && exportStatus?.[exportOpen.id] && <ExportEventDialog info={exportStatus[exportOpen.id]} cfg={exportCfg} sku={exportOpen.sku} onClose={() => setExportOpen(null)} />}
      {regenRow && <RegenDialog row={regenRow} order={order} onClose={() => { setRegenRow(null); invalidate('readiness:rows:'); loadRows(); }} />}
      <p className="mt-6 text-2xs text-ink-400 dark:text-ink-500 flex items-center gap-1.5"><Clock className="w-3 h-3" /> Stan zapisuje silnik po każdym biegu i po „Odśwież statusy”.</p>
      {pdfHtml && <PdfPreviewModal doc={pdfHtml} onClose={() => setPdfHtml(null)} />}
    </div>
  );
}
